/**
 * Ops/QA — layer-stack inspection (OPS-QA-UI).
 *
 * Two views plus per-layer build triggers (W29-ADMIN-BUILD):
 * builders run synchronously and return their JSON summary.
 *
 * - Match (Layer Stack): pick a game, see one panel per layer
 *   (L1 → L2 → L3 → L4 → L5 → L6 → serve) with artifact state,
 *   row counts and persisted run summaries.  The Pipeline DAG is
 *   a collapsible right panel inside this view — one page, no
 *   tab switching.  A game with nothing built renders "not built"
 *   per layer — that is a true statement, not an error.
 * - Layer: pick one table, see it across every stack game.
 *
 * Warning counters (off_pitch, ball_missing, side_source, …) are real
 * column scans over local artifacts, so they run only on the explicit
 * "Compute warnings" action and the button says so. Counters the
 * stack does not persist (dropped_team_rows, builder check output)
 * are shown as "not persisted", never as zero.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'

import { API_BASE, ApiError, api, type Session } from '../lib/api'
import { servedFps } from '../lib/frameRate'
import { ClubLogo } from '../components/ClubLogo'
import { BuildBoard } from './BuildBoard'
import LayerStackGuide from './LayerStackGuide'
import { MatchQuality } from './MatchQuality'
import { LayersLabIndex, StackSummaryCard } from './LayersLabIndex'
import { CHIP_COLORS, notGreenReason, relativeTime } from './layersLabModel'
import { ActionLogPanel, ActionLogProvider, useActionLog } from './actionLog'
import { GameNotes } from './GameNotes'
import { RerunTrdaas } from './RerunTrdaas'
import { ReviewToggle } from './ReviewToggle'
import { ServeToggle } from './ServeToggle'
import { TurnaroundPanel } from './TurnaroundPanel'
import { VIDEO_TIMES_SAVED } from './videoTimesMessage'
import { DataDictionaryView } from '../pages/DataDictionary'
import { BuildRunsProvider, Elapsed, WorkerActivity, secs, useBuildRuns, waitingFor, type BuildRun } from './buildRuns'
import type { RunStatus } from './buildRuns'
import type { ChipStatus, LayerKey, MatchRow } from './layersLabModel'
import '../pages/admin.css'
import '../components/club-logo.css'
import './layer-stack.css'
import { clockToSecs, secsToField } from '../components/MatchMarks'

// What a reviewer checks before marking a match "Human reviewed" (Ben,
// 2026-10-24): guidance, not enforcement — the review record and its API
// are unchanged. Each item names something this page or Match Lab shows;
// docs/human-review-pipeline.md carries the same list with the reason for
// each, and a test holds the two to the same words.
export const REVIEW_CHECKLIST = [
  'The away and home teams in the heading are the right clubs, the right way round.',
  "The score matches the real final result, and Match Lab's goal labels add up to it.",
  'Each Timings clip (kick off, end of first half, second-half kick off, end of game) shows that moment on the video.',
  "Home attacks the same way on Match Lab's pitch as on the video in both halves, and H1 dir on this page agrees.",
  'In Match Lab, the pitch keeps pace with the video near the start and near the end of each half.',
  "Match Lab's Goals and Shots passes are worked through, each event at the right moment and with the right team.",
  'No chip on the strip (L1–L6, S, Timings) is red, and any yellow chip is explained in your comment.',
  'The serve switch is set the way you intend.',
] as const

function ReviewChecklist() {
  return (
    <details className="ls-flag ls-checklist" data-testid="review-checklist">
      <summary>What to check before marking reviewed</summary>
      <div className="ls-checklist__body">
        <p className="muted">
          Sign off in Match Lab only after each of these, and say in the
          comment what you checked.
        </p>
        <ol>
          {REVIEW_CHECKLIST.map((item) => <li key={item}>{item}</li>)}
        </ol>
      </div>
    </details>
  )
}

type StackGame = {
  game_id: number
  vendors: string[]
  l1_batches: number
  first_received_at: string | null
  match_date: string | null
  home_team: string | null
  away_team: string | null
  stack_status?: string  // none | ingested | promoted | partial | ready
  organization_id?: number | null
}

// How far a game has got through the ops build pipeline. Internal only:
// none of these says anything about what customers can see.
const STACK_STATUS_LABELS: Record<string, string> = {
  none: 'nothing ingested',
  ingested: 'tracking ingested',
  promoted: 'imported, no events yet',   // clean_game row exists
  partial: 'events built',                // L4 possession rows exist
  ready: 'serve files built',
}

type OpsOrganization = {
  id: number
  name: string
  code: string
  logo_url?: string | null
}

type ParquetFacts = {
  built: boolean
  files: number
  bytes: number
  rows: number | null
  modified_at?: number
}

type Batch = {
  ingest_batch_id: number
  feed_type: string
  vendor: string
  status: string
  content_sha256: string
  received_at: string | null
  source_uri: string
}

type AnalysisRun = {
  run_id: number
  analysis_id: string
  contract_version: number
  model_version_id: number | null
  tier: string
  is_current: boolean
  run_at: string | null
  failed_checks: unknown
  coverage_snapshot: unknown
}

type LayersPayload = {
  game_id: number
  game_prefix: string | null
  video_path: string | null
  tracking_path: string | null
  storage_root_configured: boolean
  parquet_note: string | null
  layers: {
    l1: { built: boolean; batches: Batch[]
          entity_rows: Record<string, number>
          files: { key: string; bytes: number }[] }
    l2: { built: boolean; tables: Record<string, ParquetFacts> }
    l3: { built: boolean; tables: Record<string, ParquetFacts> }
    l4: { built: boolean; counts: Record<string, number>
          action_types: Record<string, number> }
    l5: { built: boolean; counts: Record<string, number> }
    l6: { present: boolean; built: boolean
          counts: Record<string, number | null>; note: string | null }
    serve: { built: boolean
             overlays: Record<string, { built: boolean; bytes: number
                                        rows: number | null }> }
  }
  analysis_runs: AnalysisRun[]
}

type WarningsPayload = {
  game_id: number
  side_sources: Record<string, Record<string, number>>
  scans: Record<string, Record<string, number | null>>
  scan_note: string | null
  not_persisted: Record<string, string>
}

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-US')

const bytes = (n: number | null | undefined) =>
  n == null ? '—'
    : n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB`
      : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`

// hideEmpty: a GREEN layer hides tables that hold nothing for this game
// (another vendor's tables, optional outputs) — they carry no signal there.
// Yellow/red layers keep them: the missing table is why it is not green.
function CountsTable({ counts, hideEmpty = false }: {
  counts: Record<string, number | null>; hideEmpty?: boolean
}) {
  const keys = Object.keys(counts).filter((k) => !hideEmpty || !!counts[k])
  if (keys.length === 0) return null
  return (
    <table className="adm-table">
      <thead><tr><th>Table</th><th>Rows</th></tr></thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k}><td>{k}</td>
            <td className="t-data">{fmt(counts[k])}</td></tr>
        ))}
      </tbody>
    </table>
  )
}

function ParquetTables({ tables, hideEmpty = false }: {
  tables: Record<string, ParquetFacts>; hideEmpty?: boolean
}) {
  const all = Object.entries(tables)
  if (!all.some(([, t]) => t.built)) return null
  const entries = hideEmpty ? all.filter(([, t]) => t.built) : all
  return (
    <table className="adm-table">
      <thead>
        <tr><th>Table</th><th>State</th><th>Rows</th>
          <th>Files</th><th>Size</th></tr>
      </thead>
      <tbody>
        {entries.map(([n, t]) => (
          <tr key={n}>
            <td>{n}</td>
            <td>{t.built ? 'built' : 'not built'}</td>
            <td className="t-data">{fmt(t.rows)}</td>
            <td className="t-data">{t.built ? t.files : '—'}</td>
            <td className="t-data">{t.built ? bytes(t.bytes) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Panel({ title, built, children, defaultOpen = true, borderColor,
                 reason }: {
  title: string; built: boolean; children: React.ReactNode
  defaultOpen?: boolean; borderColor?: string
  /** why the layer is not green — shown after the title, in its color */
  reason?: string | null
}) {
  const [open, setOpen] = useState(defaultOpen)
  const leftColor = borderColor ?? (built ? '#1c7c31' : '#9e9e9e')
  return (
    <section style={{ marginBottom: 'var(--space-3)',
                      borderLeft: `3px solid ${leftColor}`,
                      paddingLeft: '8px' }}>
      <h2
        className="t-label is-clickable"
        role="button"
        tabIndex={0}
        style={{ marginBottom: open ? 'var(--space-2)' : 0, cursor: 'pointer',
                 userSelect: 'none', display: 'flex', alignItems: 'center' }}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(!open) }}
      >
        {open ? '▾' : '▸'} {title}
        {reason && (
          <span style={{ color: leftColor, textTransform: 'none',
                         letterSpacing: 0, fontWeight: 400,
                         marginLeft: '0.4em' }}>
            — {reason}
          </span>
        )}
      </h2>
      {open && children}
    </section>
  )
}

// ── W30-CORRECTIONS: correction types and panel ──────────────────────────

type Correction = {
  assertion_id: number
  game_id: number
  org_id: number
  user_id: number
  role: string
  surface: string
  target_type: string
  target_ref: { track_id: number; frame_start: number; frame_end: number }
  action: string
  payload: { field: string; old_value: unknown; new_value: unknown }
  reason: string | null
  supersedes_assertion_id: number | null
  created_at: string | null
}

function CorrectionForm({ gameId, onSubmitted }: {
  gameId: number; onSubmitted: () => void
}) {
  const [trackId, setTrackId] = useState('')
  const [frameStart, setFrameStart] = useState('')
  const [frameEnd, setFrameEnd] = useState('')
  const [field, setField] = useState<'jersey' | 'player'>('jersey')
  const [oldValue, setOldValue] = useState('')
  const [newValue, setNewValue] = useState('')
  const [reason, setReason] = useState<'wrong' | 'missing' | 'other'>('wrong')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const { log } = useActionLog()

  const submit = useCallback(() => {
    setSubmitting(true); setSubmitError(null)
    const body = {
      track_id: Number(trackId),
      frame_start: Number(frameStart),
      frame_end: Number(frameEnd),
      field,
      old_value: field === 'jersey' ? Number(oldValue) : oldValue,
      new_value: field === 'jersey' ? Number(newValue) : newValue,
      reason,
    }
    api<{ assertion_id: number }>(
      `/api/v1/layerstack/games/${gameId}/corrections`,
      { method: 'POST', body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' } })
      .then(() => {
        setSubmitting(false)
        log('done', `Correction filed: track ${trackId} ${field} ${oldValue} → ${newValue}`)
        setTrackId(''); setFrameStart(''); setFrameEnd('')
        setOldValue(''); setNewValue('')
        onSubmitted()
      })
      .catch((e: unknown) => {
        const m = e instanceof ApiError ? e.message : String(e)
        setSubmitError(m)
        log('fail', `Correction: FAILED — ${m}`)
        setSubmitting(false)
      })
  }, [gameId, trackId, frameStart, frameEnd, field, oldValue, newValue, reason, onSubmitted, log])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px',
                  padding: '8px', border: '1px solid var(--color-border, #ccc)',
                  borderRadius: '4px', marginBottom: 'var(--space-2)' }}>
      <span className="t-label" style={{ fontSize: '12px' }}>
        Report correction
      </span>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Track ID</span>
          <input className="adm-input" type="number" value={trackId}
                 style={{ width: '80px' }}
                 onChange={(e) => setTrackId(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Frame start</span>
          <input className="adm-input" type="number" value={frameStart}
                 style={{ width: '80px' }}
                 onChange={(e) => setFrameStart(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Frame end</span>
          <input className="adm-input" type="number" value={frameEnd}
                 style={{ width: '80px' }}
                 onChange={(e) => setFrameEnd(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Field</span>
          <select className="adm-select" value={field}
                  onChange={(e) => setField(e.target.value as 'jersey' | 'player')}>
            <option value="jersey">Jersey</option>
            <option value="player">Player</option>
          </select>
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Old value</span>
          <input className="adm-input" value={oldValue}
                 style={{ width: '80px' }}
                 onChange={(e) => setOldValue(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">New value</span>
          <input className="adm-input" value={newValue}
                 style={{ width: '80px' }}
                 onChange={(e) => setNewValue(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '0 0 auto' }}>
          <span className="t-label">Reason</span>
          <select className="adm-select" value={reason}
                  onChange={(e) => setReason(e.target.value as 'wrong' | 'missing' | 'other')}>
            <option value="wrong">Wrong</option>
            <option value="missing">Missing</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      <div>
        <button className="adm-btn" disabled={submitting || !trackId || !newValue}
                onClick={submit}>
          {submitting ? 'Submitting...' : 'Submit correction'}
        </button>
        {submitError && <span style={{ color: 'var(--color-danger, #e53e3e)',
                                       fontSize: '11px', marginLeft: '8px' }}>
          {submitError}</span>}
      </div>
    </div>
  )
}

function CorrectionsPanel({ gameId }: { gameId: number }) {
  const [corrections, setCorrections] = useState<Correction[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setError(null)
    api<{ corrections: Correction[] }>(
      `/api/v1/layerstack/games/${gameId}/corrections`)
      .then((d) => setCorrections(d.corrections))
      .catch((e: unknown) => {
        // 404 means flag is off — show nothing, not an error
        if (e instanceof ApiError && e.status === 404) {
          setCorrections(null)
          return
        }
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [gameId])

  useEffect(() => { load() }, [load])

  if (error) return <p className="error">{error}</p>
  // If corrections is null (flag off), render nothing
  if (corrections === null) return null

  return (
    <Panel title="Corrections" built={corrections.length > 0} defaultOpen={false}>
      <CorrectionForm gameId={gameId} onSubmitted={load} />
      {corrections.length === 0 ? (
        <p className="muted">No corrections recorded for this game.</p>
      ) : (
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th><th>Track</th><th>Frames</th>
              <th>Field</th><th>Old</th><th>New</th>
              <th>Reason</th><th>Created (UTC)</th>
            </tr>
          </thead>
          <tbody>
            {corrections.map((c) => (
              <tr key={c.assertion_id}>
                <td className="t-data">{c.assertion_id}</td>
                <td className="t-data">{c.target_ref?.track_id ?? '—'}</td>
                <td className="t-data">
                  {c.target_ref
                    ? `${c.target_ref.frame_start}–${c.target_ref.frame_end}`
                    : '—'}
                </td>
                <td>{c.payload?.field ?? '—'}</td>
                <td className="t-data">{String(c.payload?.old_value ?? '—')}</td>
                <td className="t-data">{String(c.payload?.new_value ?? '—')}</td>
                <td>{c.reason ?? '—'}</td>
                <td className="t-data">{c.created_at ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

// ── End W30-CORRECTIONS ───────────────────────────────────────────────────

// ── W34-ADMIN-LAB: per-table stack summary ─────────────────────────────────

type StackTableEntry = {
  layer: string
  kind: string
  rows: number | null
  bytes: number | null
  files?: number
  built?: boolean
}

type LineageEntry = {
  run_id: number
  analysis_id: string
  contract_version: number
  model_version_id: number | null
  tier: string
  is_current: boolean
  run_at: string | null
  failed_checks: unknown
  model_name: string | null
  model_version: string | null
  params: Record<string, unknown> | null
}

type StackSummaryPayload = {
  game_id: number
  game_prefix: string | null
  tables: Record<string, StackTableEntry>
  lineage: Record<string, LineageEntry>
  facts: Record<string, Record<string, unknown>>
}

function StackSummaryPanel({ gameId }: { gameId: number }) {
  const [data, setData] = useState<StackSummaryPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    api<StackSummaryPayload>(`/api/v1/opsqa/games/${gameId}/stack-summary`)
      .then((d) => { setData(d); setLoading(false); setOpen(true) })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : String(e))
        setLoading(false)
      })
  }, [gameId])

  useEffect(() => { setData(null); setOpen(false) }, [gameId])

  if (!open && !data) {
    return (
      <section style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="t-label">Per-table stack summary</h2>
        <button className="adm-btn" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Load stack summary'}
        </button>
        {error && <p className="error">{error}</p>}
      </section>
    )
  }

  if (!data) return null

  // Group tables by layer
  const byLayer: Record<string, [string, StackTableEntry][]> = {}
  for (const [name, entry] of Object.entries(data.tables)) {
    const l = entry.layer || 'unknown'
    ;(byLayer[l] ??= []).push([name, entry])
  }
  const layerOrder = ['serve', 'l6', 'l5', 'l4', 'l3', 'l2', 'l1']
  const sortedLayers = Object.keys(byLayer).sort((a, b) => {
    const ai = layerOrder.indexOf(a)
    const bi = layerOrder.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  return (
    <Panel title="Per-table stack summary" built={true}
           defaultOpen={open}>
      {sortedLayers.map((layer) => {
        const tables = byLayer[layer]!
        return (
          <div key={layer} style={{ marginBottom: 'var(--space-2)' }}>
            <h3 className="t-label" style={{ fontSize: '12px',
              marginBottom: 'var(--space-1)' }}>{layer}</h3>
            <table className="adm-table">
              <thead>
                <tr><th>Table</th><th>Kind</th><th>Rows</th>
                  <th>Size</th><th>Files</th></tr>
              </thead>
              <tbody>
                {tables.map(([name, t]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{t.kind}</td>
                    <td className="t-data">{fmt(t.rows)}</td>
                    <td className="t-data">{t.kind === 'parquet'
                      ? bytes(t.bytes) : '—'}</td>
                    <td className="t-data">{t.files ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}

      {/* Lineage */}
      {Object.keys(data.lineage).length > 0 && (
        <div style={{ marginBottom: 'var(--space-2)' }}>
          <h3 className="t-label" style={{ fontSize: '12px',
            marginBottom: 'var(--space-1)' }}>Lineage (latest per analysis)</h3>
          <table className="adm-table">
            <thead>
              <tr><th>Analysis</th><th>Run</th><th>Model</th>
                <th>Version</th><th>Tier</th><th>Current</th>
                <th>Run at</th><th>Checks</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.lineage).map(([, l]) => (
                <tr key={l.run_id}>
                  <td>{l.analysis_id}</td>
                  <td className="t-data">{l.run_id}</td>
                  <td>{l.model_name ?? '—'}</td>
                  <td>{l.model_version ?? '—'}</td>
                  <td>{l.tier}</td>
                  <td>{l.is_current ? 'yes' : 'no'}</td>
                  <td className="t-data">{l.run_at ?? '—'}</td>
                  <td className="t-data">
                    <ChecksCell tier={l.tier} checks={l.failed_checks} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Expandable params */}
          {Object.entries(data.lineage).some(([, l]) => l.params) && (
            <details style={{ fontSize: '11px', marginTop: 'var(--space-1)' }}>
              <summary style={{ cursor: 'pointer' }}>
                Model params (expand)
              </summary>
              {Object.entries(data.lineage)
                .filter(([, l]) => l.params)
                .map(([, l]) => (
                <div key={l.run_id} style={{ marginTop: '4px' }}>
                  <strong>{l.analysis_id}</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '2px 0' }}>
                    {JSON.stringify(l.params, null, 2)}
                  </pre>
                </div>
              ))}
            </details>
          )}
        </div>
      )}

      {/* Interesting facts */}
      {Object.keys(data.facts).length > 0 && (
        <div>
          <h3 className="t-label" style={{ fontSize: '12px',
            marginBottom: 'var(--space-1)' }}>Measured facts</h3>
          <table className="adm-table">
            <thead>
              <tr><th>Table</th><th>Facts</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.facts).map(([table, info]) => (
                <tr key={table}>
                  <td>{table}</td>
                  <td className="t-data">
                    {Object.entries(info)
                      .map(([k, v]) => `${k}=${v}`)
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

// ── End W34-ADMIN-LAB stack summary ───────────────────────────────────────

type MatchInfo = {
  home_team: string | null; away_team: string | null
  home_attack_dir_h1: string | null; fps: number | null
  periods: { period_id: number; start_frame_idx: number
             end_frame_idx: number; home_attacks_positive_x: boolean }[]
  teams: Record<string, number>
  goals: { goal_id: number; frame_idx: number; team_id: number | null }[]
  video_game_start_time: number | null
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_normaltime_end_time: number | null
}

/** An analysis run's checks: a refused build (tier insufficient,
 *  not_measured.py) keeps what is missing in failed_checks — it is not
 *  measured, in orange, never a failed check in red (SDET, PR #694). */
export function ChecksCell({ tier, checks }: { tier: string; checks: unknown }) {
  if (tier === 'insufficient') {
    const missing = (checks as { missing_input?: string } | null)?.missing_input
    return <span style={{ color: '#f97316' }}>
      Not measured{missing ? `: ${missing}` : ''}</span>
  }
  // A run measured with a note (tier partial) keeps the note there:
  // built, with the note, never a failed check.
  if (tier === 'partial') {
    const reason = (checks as { reason?: string } | null)?.reason
    return <span style={{ color: STATUS_COLORS.noted }}>
      Built, with a note{reason ? `: ${reason}` : ''}</span>
  }
  if (checks == null) return <>none</>
  return <span style={{ color: 'var(--color-danger, #e53e3e)' }}>
    {JSON.stringify(checks)}</span>
}

// The clock helpers moved to components/MatchMarks (calibration review
// marks with them too); re-exported so existing imports keep working.
export { clockToSecs, secsToField }

/** The match clock: the four moments an operator reads off the video.
 *  The server computes both halves' frames from them
 *  (ops_qa_api._apply_match_clock) — nobody types a frame number. */
export function MatchClock({ facts, saving, save }: {
  facts: GameFacts
  saving: boolean
  save: (body: Record<string, unknown>) => void
}) {
  const p1 = facts.periods.find((p) => p.period_id === 1)
  const p2 = facts.periods.find((p) => p.period_id === 2)
  const fields = [
    { key: 'kickoff_s', label: 'Kick-off', init: p1?.video_start_s },
    { key: 'halftime_s', label: 'Half-time', init: p1?.video_end_s },
    { key: 'second_half_s', label: '2H kick-off', init: p2?.video_start_s },
    { key: 'fulltime_s', label: 'Full time', init: p2?.video_end_s },
  ]
  const [vals, setVals] = useState(() => fields.map((f) => secsToField(f.init)))
  const secs = vals.map(clockToSecs)
  const ordered = secs.every((x) => x != null)
    && secs[0]! < secs[1]! && secs[1]! <= secs[2]! && secs[2]! < secs[3]!
  const small = { fontSize: '11px', padding: '1px 4px', width: '80px' }
  return (
    <div data-testid="match-clock"
         style={{ display: 'flex', gap: '10px', alignItems: 'center',
                  flexWrap: 'wrap', padding: '4px 0 8px', fontSize: '12px' }}>
      <strong>Match clock</strong>
      <span className="muted" style={{ fontSize: '11px' }}>video time, m:ss or seconds</span>
      {fields.map((f, i) => (
        <label key={f.key}>
          <span className="t-label">{f.label}</span>{' '}
          <input className="adm-input" aria-label={f.label} value={vals[i]}
                 placeholder="m:ss" style={small}
                 onChange={(e) => setVals(vals.map((v, j) => j === i ? e.target.value : v))} />
        </label>
      ))}
      <button className="adm-btn" disabled={saving || !ordered}
              title={ordered ? undefined
                : 'Fill all four, in order: kick-off < half-time <= 2H kick-off < full time'}
              onClick={() => save({ match_clock: Object.fromEntries(
                fields.map((f, i) => [f.key, secs[i]])) })}>
        Set halves
      </button>
    </div>
  )
}

/** A half with no clean_match_period row (a legacy import that found no
 *  halftime): create it. Prefilled from the vendor's measured bounds when
 *  the game has them, else from 1H's end to the last frame; the server
 *  refuses bounds that overlap another period. */
export function NewPeriodRow({ label, periodId, facts, saving, save }: {
  label: string
  periodId: number
  facts: GameFacts
  saving: boolean
  save: (body: Record<string, unknown>) => void
}) {
  const other = facts.periods.find((q) => q.period_id !== periodId)
  const vendor = facts.suggested?.periods.find((q) => q.period_id === periodId)
  const last = (facts.total_frames ?? 0) - 1
  const guess = vendor
    ? [vendor.start_frame_idx, vendor.end_frame_idx]
    : periodId === 1
      ? [0, other ? other.start_frame_idx - 1 : last]
      : [other ? other.end_frame_idx + 1 : 0, last]
  const [start, setStart] = useState(String(guess[0]))
  const [end, setEnd] = useState(String(guess[1]))
  const [vs, setVs] = useState('')
  const [ve, setVe] = useState('')
  const sf = parseInt(start), ef = parseInt(end)
  const valid = !isNaN(sf) && !isNaN(ef) && sf >= 0 && sf <= ef
  const small = { fontSize: '11px', padding: '1px 4px', width: '90px' }
  return (
    <div data-testid={`new-period-${periodId}`}
         style={{ display: 'flex', gap: '10px', alignItems: 'center',
                  flexWrap: 'wrap', padding: '4px 0', fontSize: '12px' }}>
      <strong style={{ width: '24px' }}>{label}</strong>
      <span className="muted" style={{ fontSize: '11px' }}>no period row —
        {vendor ? ` vendor-measured bounds (${vendor.basis}):` : ' create it:'}</span>
      <label>
        <span className="t-label">start frame</span>{' '}
        <input className="adm-input" type="number" min="0" step="1"
               aria-label={`${label} start frame`}
               value={start} onChange={(e) => setStart(e.target.value)} style={small} />
      </label>
      <label>
        <span className="t-label">end frame</span>{' '}
        <input className="adm-input" type="number" min="0" step="1"
               aria-label={`${label} end frame`}
               value={end} onChange={(e) => setEnd(e.target.value)} style={small} />
      </label>
      <label>
        <span className="t-label">video start s</span>{' '}
        <input className="adm-input" type="number" step="0.1"
               aria-label={`${label} video start`}
               value={vs} onChange={(e) => setVs(e.target.value)} style={small} />
      </label>
      <label>
        <span className="t-label">video end s</span>{' '}
        <input className="adm-input" type="number" step="0.1"
               aria-label={`${label} video end`}
               value={ve} onChange={(e) => setVe(e.target.value)} style={small} />
      </label>
      <button className="adm-btn" disabled={saving || !valid}
              onClick={() => {
                const p: Record<string, number> = {
                  period_id: periodId, start_frame_idx: sf, end_frame_idx: ef }
                if (vs !== '' && !isNaN(parseFloat(vs))) p.video_start_s = parseFloat(vs)
                if (ve !== '' && !isNaN(parseFloat(ve))) p.video_end_s = parseFloat(ve)
                save({ periods: [p] })
              }}>
        Create {label}
      </button>
    </div>
  )
}

export type GameFacts = {
  game_id: number
  exists: boolean
  match_date: string | null
  org_side: string | null
  /** game_facts.org_side: the club's side; null = not our match */
  club_side?: 'home' | 'away' | null
  capture_fps: number | null
  total_frames: number | null
  game_code: string | null
  home_team: string | null
  away_team: string | null
  periods: {
    period_id: number
    start_frame_idx: number
    end_frame_idx: number
    home_attacks_positive_x: boolean
    video_start_s: number | null
    video_end_s: number | null
    /** 'vendor_measured' frames are the tracking's own truth: a
     *  seconds edit never moves them. Other sources re-derive. */
    source: string | null
  }[]
  /** Period bounds from the vendor's own period table, in canonical
   *  frames — null when the game has no vendor periods. */
  suggested?: {
    periods: { period_id: number; start_frame_idx: number
               end_frame_idx: number; basis: string }[]
    total_frames: number
  } | null
  /** Home/away and half-1 direction from L1 (raw_game_submission):
   *  null when L1 could not be read; absent from older APIs. */
  identity?: GameIdentity | null
  /** Which way tracking team 0 attacks in half 1, measured from the
   *  players' camera positions; null when L3 cannot be read. Absent
   *  from older APIs. */
  team0_half1?: Team0Half1 | null
  /** The old system's home team number (game_facts); null = none
   *  recorded, which every reader takes as 1. */
  legacy_home_index?: number | null
  /** The game's pitch (game_facts); absent from older APIs. */
  pitch?: PitchFacts
  /** The four marks on the game's record (game_files), which l2's
   *  frame-rate check reads; null with no game_files row. */
  record_marks?: Record<'kickoff_s' | 'halftime_s' | 'second_half_s' | 'fulltime_s',
                        number | null> | null
}

export type PitchFacts = {
  length_cm: number | null
  width_cm: number | null
  source: string | null
  /** true: the platform default, not this venue's size */
  assumed: boolean
  /** Only trdaas L3 builds from it; other vendors' files declare it. */
  editable: boolean
  vendor: string | null
}

/** Metres typed into Game Facts as whole centimetres, or an error. Both
 *  are needed, in the Laws' range (90–120 × 45–90 m). */
export function pitchFromMetres(length: string, width: string):
    { pitch_length_cm: number; pitch_width_cm: number } | { error: string } {
  const l = Math.round(parseFloat(length) * 100)
  const w = Math.round(parseFloat(width) * 100)
  if (isNaN(l) || isNaN(w)) return { error: 'Give both length and width, in metres.' }
  if (l < 9000 || l > 12000) return { error: 'Length must be 90–120 m.' }
  if (w < 4500 || w > 9000) return { error: 'Width must be 45–90 m.' }
  return { pitch_length_cm: l, pitch_width_cm: w }
}

function PitchSize({ pitch, saving, save }: {
  pitch: PitchFacts | undefined; saving: boolean
  save: (body: Record<string, unknown>) => void
}) {
  const m = (cm: number | null | undefined) => (cm ? (cm / 100).toFixed(2) : '')
  const [length, setLength] = useState(m(pitch?.length_cm))
  const [width, setWidth] = useState(m(pitch?.width_cm))
  const [error, setError] = useState<string | null>(null)
  if (!pitch) return <span className="muted">unknown</span>
  const shown = pitch.length_cm && pitch.width_cm
    ? `${m(pitch.length_cm)} × ${m(pitch.width_cm)} m` : 'not set'
  const note = pitch.assumed ? ' (platform default — not this venue)'
    : pitch.source ? ` (${pitch.source})` : ''
  if (!pitch.editable) {
    return (
      <span title="Tracab and Second Spectrum files declare the pitch; L3 uses theirs">
        {shown}{note}
        <span className="muted"> — L3 uses the pitch the {pitch.vendor ?? 'tracking'} files declare</span>
      </span>
    )
  }
  const changed = length !== m(pitch.length_cm) || width !== m(pitch.width_cm)
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px',
                   flexWrap: 'wrap' }}>
      <input className="adm-input" type="number" step="0.01" min="90" max="120"
             aria-label="Pitch length (m)" value={length}
             onChange={(e) => { setLength(e.target.value); setError(null) }}
             style={{ fontSize: '12px', padding: '2px 6px', width: '80px' }} />
      ×
      <input className="adm-input" type="number" step="0.01" min="45" max="90"
             aria-label="Pitch width (m)" value={width}
             onChange={(e) => { setWidth(e.target.value); setError(null) }}
             style={{ fontSize: '12px', padding: '2px 6px', width: '80px' }} />
      m
      <button className="adm-btn" style={{ fontSize: '11px' }}
              disabled={saving || !changed}
              title="Saves the size; L3 and everything after it rebuild with Force"
              onClick={() => {
                const r = pitchFromMetres(length, width)
                if ('error' in r) setError(r.error)
                else save(r)
              }}>
        Save pitch
      </button>
      <span className="muted">{note.trim()}</span>
      {error && <span role="alert" style={{ color: '#ef4444' }}>{error}</span>}
    </span>
  )
}

export type GameIdentity = {
  /** The real home/away names people recorded (legacy import or an
   *  operator), when any; absent from older APIs. */
  home_team?: string | null
  away_team?: string | null
  /** The tracking vendor's own home/away names. */
  vendor_home_team: string | null
  vendor_away_team: string | null
  /** false: an operator said the vendor's home team is really away. */
  home_is_vendor_home: boolean
  /** An operator's half-1 direction; null = L3 measures it. */
  home_attack_dir_h1: 'left' | 'right' | null
  submitted_by: string | null
  submitted_at: string | null
  /** What the old system recorded — shown, never applied by itself. */
  legacy_home_attack_dir_h1: 'left' | 'right' | null
}

export type Team0Half1 = {
  /** null: the geometry could not say (why says why) */
  attacks: 'right' | 'left' | null
  why: string | null
  team: string | null
  /** the side the stack holds tracking team 0 to be */
  side: 'home' | 'away' | null
  /** the way L3 has home attacking on video in half 1 */
  home_attacks_right: boolean | null
}

type Dir = 'right' | 'left'
const flip = (d: Dir): Dir => (d === 'right' ? 'left' : 'right')
const arrow = (d: Dir) => (d === 'right' ? '\u2192 right' : '\u2190 left')

/** Team 0's measured half-1 direction against what each record implies
 *  for it: the stack (team 0's side, home's half-1 direction in L3) and
 *  the old system (its home team number, 1 when none is recorded, and
 *  its half-1 direction). expected is null where a record cannot say. */
export function team0Check(t0: Team0Half1, legacyHomeIndex: number | null,
                           legacyDir: Dir | null): {
  stack: { expected: Dir | null; agrees: boolean | null }
  legacy: { homeIndex: number; expected: Dir | null; agrees: boolean | null }
} {
  const implied = (teamIsHome: boolean | null, homeDir: Dir | null) =>
    teamIsHome == null || homeDir == null ? null
      : teamIsHome ? homeDir : flip(homeDir)
  const verdict = (expected: Dir | null) =>
    expected == null || t0.attacks == null ? null : expected === t0.attacks
  const stackDir: Dir | null = t0.home_attacks_right == null ? null
    : t0.home_attacks_right ? 'right' : 'left'
  const stack = implied(t0.side == null ? null : t0.side === 'home',
                        stackDir)
  const homeIndex = legacyHomeIndex === 0 ? 0 : 1
  const legacy = implied(homeIndex === 0, legacyDir)
  return { stack: { expected: stack, agrees: verdict(stack) },
           legacy: { homeIndex, expected: legacy, agrees: verdict(legacy) } }
}

function Verdict({ agrees, expected }: { agrees: boolean | null
                                         expected: Dir | null }) {
  if (agrees == null) return null
  return agrees
    ? <span style={{ color: 'var(--color-success, #22c55e)' }}> — agrees</span>
    : <strong style={{ color: 'var(--color-danger, #e53e3e)' }}>
        {' '}— disagrees: team 0 would attack {expected && arrow(expected)}
      </strong>
}

/** Tracking team 0's half-1 direction as measured, beside who each
 *  record says is home and which way home attacks in half 1 — a game
 *  whose home is the wrong team reads as "disagrees". */
export function Team0Direction({ t0, legacyHomeIndex, legacyDir }: {
  t0: Team0Half1 | null | undefined
  legacyHomeIndex: number | null | undefined
  legacyDir: Dir | null
}) {
  if (t0 === undefined) return null
  if (t0 === null) {
    return <p className="muted" style={{ fontSize: '11px' }}>
      Tracking team 0's direction could not be read from L3.
    </p>
  }
  const c = team0Check(t0, legacyHomeIndex ?? null, legacyDir)
  const stackDir: Dir | null = t0.home_attacks_right == null ? null
    : t0.home_attacks_right ? 'right' : 'left'
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                  gap: '4px 12px', fontSize: '12px', maxWidth: '560px',
                  marginBottom: '8px' }}>
      <span className="t-label">Tracking team 0</span>
      <span>
        <strong>{t0.team ?? 'team 0'}</strong>
        {t0.attacks
          ? <> attacks {arrow(t0.attacks)} in 1H
              <span className="muted"> (measured from where the keepers stand)</span></>
          : <span className="muted"> — direction not measured: {t0.why}</span>}
      </span>

      <span className="t-label">Stack</span>
      <span>
        {t0.side ? `holds team 0 as ${t0.side}` : 'team 0 has no side'}
        {stackDir ? `, home attacks ${arrow(stackDir)} in 1H`
                  : ', no 1H direction in L3'}
        <Verdict {...c.stack} />
      </span>

      <span className="t-label">Old record</span>
      <span>
        home is its team {c.legacy.homeIndex}
        {legacyHomeIndex == null && <span className="muted"> (none recorded, read as 1)</span>}
        {legacyDir ? `, home attacks ${arrow(legacyDir)} in 1H`
                   : ', no 1H direction recorded'}
        <Verdict {...c.legacy} />
        <span className="muted" style={{ display: 'block',
                                         fontSize: '10px' }}>
          The old system numbers teams per feed, so its team 0 may not be
          the vendor's team 0.
        </span>
      </span>
    </div>
  )
}

type FactsSaveResult = {
  updated: string[]
  rebuild?: { queued: true; batch_id: string }
    | { queued: false; code: string; message: string }
  /** The frames the server computed from a match clock; empty periods
   *  when the vendor measured both halves and its frames were kept. */
  match_clock?: { how: string; periods: { period_id: number
                  start_frame_idx: number; end_frame_idx: number }[] }
}

/** The save's outcome in words: a queued rebuild is part of it, and a
 *  rebuild that could not be queued is a failure the operator must see. */
export function factsSaveMessage(r: FactsSaveResult): {
  ok: boolean; text: string
} {
  let saved = `Saved: ${r.updated.join(', ') || 'nothing changed'}`
  if (r.match_clock) {
    saved += r.match_clock.periods.length
      ? ' — frames ' + r.match_clock.periods.map((p) =>
          `${p.period_id === 1 ? '1H' : '2H'} ${p.start_frame_idx.toLocaleString()}`
          + `–${p.end_frame_idx.toLocaleString()}`).join(', ')
      : ' — frames kept (measured by the vendor)'
  }
  if (!r.rebuild) return { ok: true, text: saved }
  if (r.rebuild.queued) {
    return { ok: true,
             text: `${saved} — Force rebuild from L3 queued (${r.rebuild.batch_id.slice(0, 8)})` }
  }
  return { ok: false,
           text: `${saved}, but the rebuild from L3 was NOT queued: ${r.rebuild.message} — build from L3 with Force` }
}

/** Home/away and half-1 direction: an operator's word, kept in L1 so
 *  every rebuild follows it. A change rebuilds L3 and everything after. */
export function IdentityFacts({ identity, saving, save }: {
  identity: GameIdentity | null | undefined
  saving: boolean
  save: (body: Record<string, unknown>) => void
}) {
  if (identity === undefined) return null
  if (identity === null) {
    return <p className="muted" style={{ fontSize: '11px' }}>
      Home/away could not be read from L1.
    </p>
  }
  const vh = identity.vendor_home_team ?? 'vendor home team'
  const va = identity.vendor_away_team ?? 'vendor away team'
  const [home, away] = identity.home_team && identity.away_team
    ? [identity.home_team, identity.away_team]
    : identity.home_is_vendor_home ? [vh, va] : [va, vh]
  // Swapped: the team the vendor calls home is the real away team.
  const vendorHome = identity.vendor_home_team ?? away
  const legacy = identity.legacy_home_attack_dir_h1
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                  gap: '4px 12px', fontSize: '12px', maxWidth: '560px',
                  marginBottom: '8px' }}>
      <span className="t-label">Home / away</span>
      <span>
        <strong>{home}</strong><span className="muted"> (home)</span>
        <span style={{ opacity: 0.5, margin: '0 6px' }}>vs</span>
        <strong>{away}</strong><span className="muted"> (away)</span>
        <button className="adm-btn" disabled={saving}
                style={{ fontSize: '11px', padding: '1px 8px',
                         marginLeft: '8px' }}
                title="Record that the other team is home. Rebuilds L3 and everything after it."
                onClick={() => save({
                  home_is_vendor_home: !identity.home_is_vendor_home })}>
          Swap home / away
        </button>
        {!identity.home_is_vendor_home && (
          <span className="muted" style={{ display: 'block',
                                           fontSize: '10px' }}>
            Swapped by an operator — the tracking vendor says {vendorHome} is home.
          </span>
        )}
      </span>

      <span className="t-label">1H direction</span>
      <span>
        <select className="adm-select" disabled={saving}
                aria-label="Half 1 home attack direction"
                value={identity.home_attack_dir_h1 ?? ''}
                style={{ fontSize: '12px' }}
                onChange={(e) => save({
                  home_attack_dir_h1: e.target.value || null })}>
          <option value="">Measured by L3</option>
          <option value="right">Home attacks {'\u2192'} right</option>
          <option value="left">Home attacks {'\u2190'} left</option>
        </select>
        {legacy && !identity.home_attack_dir_h1 && (
          <span className="muted" style={{ marginLeft: '6px',
                                           fontSize: '10px' }}>
            the old system recorded {legacy} — not applied unless chosen
          </span>
        )}
      </span>

      <span className="muted" style={{ gridColumn: '1 / -1',
                                       fontSize: '10px' }}>
        Kept in L1{identity.submitted_by
          ? ` (last set by ${identity.submitted_by === 'ops' ? 'an operator' : 'the legacy import'})`
          : ''}; a change rebuilds L3 and everything after it with Force.
      </span>
    </div>
  )
}

/** What to change so the stored timing matches the tracking data: the
 *  vendor-derived frame bounds wherever the stored ones differ, and the
 *  exact save body that applies them. Empty when nothing to suggest. */
export function timingFixes(facts: GameFacts): {
  lines: string[]; body: Record<string, unknown> | null
} {
  const sug = facts.suggested
  if (!sug) return { lines: [], body: null }
  // the game's capture rate; with none the frame span is not given in minutes
  const fps = servedFps(facts.capture_fps)
  const lines: string[] = []
  const periods: Record<string, unknown>[] = []
  for (const sp of sug.periods) {
    const cur = facts.periods.find((p) => p.period_id === sp.period_id)
    if (cur && cur.start_frame_idx === sp.start_frame_idx
        && cur.end_frame_idx === sp.end_frame_idx) continue
    const label = sp.period_id === 1 ? '1H' : sp.period_id === 2 ? '2H'
      : `Period ${sp.period_id}`
    const mins = fps === null ? null
      : (sp.end_frame_idx - sp.start_frame_idx + 1) / fps / 60
    const video = cur && cur.video_start_s != null && cur.video_end_s != null
      ? ` — video span ${((cur.video_end_s - cur.video_start_s) / 60).toFixed(1)} min`
      : ''
    lines.push(`${label} frames → ${fmt(sp.start_frame_idx)} – ${fmt(sp.end_frame_idx)}`
      + ` (${mins === null ? '' : `${mins.toFixed(1)} min, `}${sp.basis})${video}`
      + (cur ? `; stored ${fmt(cur.start_frame_idx)} – ${fmt(cur.end_frame_idx)}`
        // Apply can only update an existing row, so say what will.
        : '; no stored row for this period — Apply cannot add one'))
    if (cur) periods.push({ period_id: sp.period_id,
                            start_frame_idx: sp.start_frame_idx,
                            end_frame_idx: sp.end_frame_idx })
  }
  const body: Record<string, unknown> = {}
  if (periods.length) body.periods = periods
  if (facts.total_frames !== sug.total_frames) {
    lines.push(`Total frames → ${fmt(sug.total_frames)} (delivered tracking frames); stored ${fmt(facts.total_frames)}`)
    body.total_frames = sug.total_frames
  }
  return { lines, body: Object.keys(body).length ? body : null }
}

// ── Game Facts editor ──────────────────────────────────────────────
// Date, FPS, frames, club side, teams, and a per-half timeline with
// switchable attack-direction arrows, frame+video timings, expandable
// Timing Clips (4 mini players), and suspect-timing reasons.

const HALF_MIN_S = 40 * 60   // a half shorter than this is suspect
const HALF_MAX_S = 65 * 60   // …or longer than this (45' + stoppage)

/** Marks on the game record (game_files) that differ from the Timeline by
 *  more than a second. l2 checks the record, and the Timeline's per-half
 *  times do not write it, so a right-looking Timeline can hide the mark
 *  l2 refuses (game 214, prod, 2026-10-16). Set halves writes both. */
export function recordMarkMismatches(facts: GameFacts): string[] {
  const rec = facts.record_marks
  if (!rec) return []
  const p1 = facts.periods.find((p) => p.period_id === 1)
  const p2 = facts.periods.find((p) => p.period_id === 2)
  const pairs: [string, number | null, number | null | undefined][] = [
    ['Kick-off', rec.kickoff_s, p1?.video_start_s],
    ['Half-time', rec.halftime_s, p1?.video_end_s],
    ['2H kick-off', rec.second_half_s, p2?.video_start_s],
    ['Full time', rec.fulltime_s, p2?.video_end_s],
  ]
  const show = (s: number | null | undefined) => (s == null ? 'not set' : secsToField(s))
  return pairs
    .filter(([, r, t]) => t != null && (r == null || Math.abs(r - t) > 1))
    .map(([label, r, t]) => `${label}: Timeline ${show(t)}, game record ${show(r)}`)
}

export function timingSuspicions(facts: GameFacts): string[] {
  const out: string[] = []
  const p1 = facts.periods.find((p) => p.period_id === 1)
  const p2 = facts.periods.find((p) => p.period_id === 2)
  // the game's capture rate; with none the frame-span check cannot run
  const fps = servedFps(facts.capture_fps)

  const missing: string[] = []
  if (!p1) missing.push('no 1H period row')
  if (!p2) missing.push('no 2H period row')
  if (p1 && p1.video_start_s == null) missing.push('1H video start not set')
  if (p1 && p1.video_end_s == null) missing.push('1H video end not set')
  if (p2 && p2.video_start_s == null) missing.push('2H video start not set')
  if (p2 && p2.video_end_s == null) missing.push('2H video end not set')
  out.push(...missing)

  for (const [label, p] of [['1H', p1], ['2H', p2]] as const) {
    if (!p || p.video_start_s == null || p.video_end_s == null) continue
    const dur = p.video_end_s - p.video_start_s
    if (dur <= 0) {
      out.push(`${label} video end (${p.video_end_s.toFixed(0)}s) is not after its start (${p.video_start_s.toFixed(0)}s)`)
    } else if (dur < HALF_MIN_S) {
      out.push(`${label} lasts ${(dur / 60).toFixed(1)} min of video — a half is normally 45+ min`)
    } else if (dur > HALF_MAX_S) {
      out.push(`${label} lasts ${(dur / 60).toFixed(1)} min of video — longer than 45 min plus plausible stoppage`)
    }
    // Frame span vs video span: > 2 min apart means the stored frame
    // range and the stored video timing disagree about the half.
    if (fps === null) continue
    const frameDur = (p.end_frame_idx - p.start_frame_idx) / fps
    if (Math.abs(frameDur - dur) > 120) {
      out.push(`${label} frame span (${(frameDur / 60).toFixed(1)} min at ${fps} fps) disagrees with its video span (${(dur / 60).toFixed(1)} min) by more than 2 min`)
    }
  }
  if (p1 && p2 && p1.video_end_s != null && p2.video_start_s != null
      && p2.video_start_s < p1.video_end_s) {
    out.push('2H starts in the video before 1H ends')
  }
  return out
}

const CLIP_MARKS = (facts: GameFacts) => {
  const p1 = facts.periods.find((p) => p.period_id === 1)
  const p2 = facts.periods.find((p) => p.period_id === 2)
  return [
    { label: 'Kick Off', time: p1?.video_start_s ?? null },
    { label: 'End of First Half', time: p1?.video_end_s ?? null },
    { label: 'Second Half Kick Off', time: p2?.video_start_s ?? null },
    { label: 'End of Game', time: p2?.video_end_s ?? null },
  ]
}

/** Four small players, one per half boundary, each seeked to the
 *  stored timestamp so an operator can eyeball whether it is right. */
function TimingClips({ videoUrl, marks }: {
  videoUrl: string | null
  marks: { label: string; time: number | null }[]
}) {
  if (!videoUrl) {
    return <p className="muted" style={{ fontSize: '11px' }}>
      No playable video for this game.
    </p>
  }
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap',
                  marginTop: '4px' }}>
      {marks.map((mk) => (
        <div key={mk.label} style={{ textAlign: 'center',
                                     minWidth: '450px',
                                     maxWidth: '100%' }}>
          <div style={{ fontSize: '10px', fontWeight: 600,
                        marginBottom: '2px' }}>{mk.label}</div>
          {mk.time != null ? (
            <>
              <video
                src={videoUrl}
                preload="metadata"
                muted
                controls
                style={{ width: '450px', height: 'auto',
                         maxWidth: '100%', borderRadius: '4px',
                         background: '#000' }}
                onLoadedMetadata={(e) => {
                  const v = e.currentTarget
                  if (mk.time != null) v.currentTime = mk.time
                }}
              />
              <div style={{ fontSize: '10px', color: '#888' }}>
                {mk.time.toFixed(1)}s
              </div>
            </>
          ) : (
            <div style={{ width: '450px', height: '253px',
                          maxWidth: '100%',
                          borderRadius: '4px', background: '#1a1a1e',
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '10px',
                          color: '#666' }}>
              not set
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** Game Facts before the game has a clean_game row. */
/** The video-times window (VideoTimes): the four marks read off the video,
 *  large, while this page stays where it is. Offered with and without a
 *  stack record — it saves wherever the game's times live. */
function openVideoTimes(gameId: number) {
  window.open(`/ops/video-times/${gameId}`, `video-times-${gameId}`,
              'width=1280,height=900')
}

/** Calls onSaved when the video-times window for this game says it saved. */
function useVideoTimesSaved(gameId: number, onSaved: () => void) {
  const latest = useRef(onSaved)
  latest.current = onSaved
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin === window.location.origin
          && e.data?.type === VIDEO_TIMES_SAVED && e.data.gameId === gameId) {
        latest.current()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [gameId])
}

function GameFactsUnavailable({ gameId, onImport, importing, children }: {
  gameId: number
  onImport?: () => void
  importing: boolean
  /** What is editable already (home/away and direction, kept in L1). */
  children?: React.ReactNode
}) {
  const runs = useBuildRuns()
  const [msg, setMsg] = useState<string | null>(null)
  // The video-times window says when it saved: Import reads those times.
  useVideoTimesSaved(gameId, () => setMsg('Match timings saved'))
  const buildToEntities = async () => {
    setMsg(null)
    try {
      // Only the steps not already built: re-publishing a built layer
      // with different bytes is refused, which would fail the batch.
      const d = await api<{ steps: PipelineStep[] }>(
        `/api/v1/opsqa/games/${gameId}/pipeline-status`)
      const need = ENTITY_PATH.filter((id) =>
        d.steps.find((s) => s.id === id)?.status !== 'built')
      if (need.length === 0) { setMsg('Entities is already built — reload'); return }
      await runs?.start(need, 'to unlock Game Facts')
    } catch (e: unknown) {
      setMsg(e instanceof ApiError ? e.message : String(e))
    }
  }
  return (
    <Panel title="Game Facts" built={false} defaultOpen={true}>
      {children}
      <p style={{ fontSize: '12px', margin: '0 0 8px' }}>
        Match date, club side and half start/end times are edited here once
        the game has its stack record (<code>clean_game</code>). This game
        has none yet. Create it either way:
      </p>
      <ul style={{ fontSize: '12px', margin: '0 0 8px', paddingLeft: '18px' }}>
        <li><strong>Build through Entities</strong> (L2 &rarr; L3 &rarr;
          Entities): periods and teams from the vendor&rsquo;s own tracking
          data. Preferred when the tracking file is ingested.</li>
        <li><strong>Import</strong>: from the legacy match record, at once.
          Period bounds may be estimates; this panel then offers the
          vendor&rsquo;s bounds once they exist.</li>
        <li><strong>Set match timings</strong> first for Import: kick off,
          half-time and end, marked on the video in its own window, and
          which way the home team attacks in the first half.</li>
      </ul>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {runs && (
          <button className="adm-btn" onClick={buildToEntities}>
            Build through Entities
          </button>
        )}
        <button className="adm-btn adm-btn--quiet"
                title="Kick off, half-time, end and home direction — Import uses them"
                onClick={() => openVideoTimes(gameId)}>
          Set match timings&hellip;
        </button>
        {onImport && (
          <button className="adm-btn" disabled={importing} onClick={onImport}>
            {importing ? 'Importing\u2026' : 'Import'}
          </button>
        )}
        {msg && <span className="muted" style={{ fontSize: '11px' }}>{msg}</span>}
      </div>
    </Panel>
  )
}

/** The steps that create clean_game from vendor data, in order. */
const ENTITY_PATH = ['l2', 'l3', 'promote-entities']

export function GameFactsPanel({ gameId, facts, videoUrl, onSaved,
                                onImport, importing = false }: {
  gameId: number
  facts: GameFacts | null
  videoUrl: string | null
  onSaved: () => void
  /** Offered while the game has no clean_game row (the header's Import). */
  onImport?: () => void
  importing?: boolean
}) {
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [clipsOpen, setClipsOpen] = useState(false)
  const { log } = useActionLog()
  const runs = useBuildRuns()
  // Times saved in the video-times window went through the match clock,
  // which may queue a rebuild: reload the facts and the runs.
  useVideoTimesSaved(gameId, () => {
    setMsg('Match timings saved')
    log('done', 'Game facts: match timings saved from the video')
    void runs?.refresh()
    onSaved()
  })

  const save = async (body: Record<string, unknown>) => {
    setSaving(true); setMsg(null)
    try {
      const r = await api<FactsSaveResult>(
        `/api/v1/opsqa/games/${gameId}/game-facts`,
        { method: 'POST', body: JSON.stringify(body) })
      const out = factsSaveMessage(r)
      setMsg(out.ok ? out.text : `Error: ${out.text}`)
      log(out.ok ? 'done' : 'fail',
          `Game facts ${out.text.replace(/^Saved/, 'saved')}`)
      // The rebuild it queued runs outside the build buttons: load it now,
      // or the Pipeline and this log miss it until something else polls.
      if (r.rebuild?.queued) void runs?.refresh()
      onSaved()
    } catch (e: unknown) {
      const m = e instanceof ApiError ? e.message : String(e)
      setMsg(`Error: ${m}`)
      log('fail', `Game facts: FAILED — ${m}`)
    }
    setSaving(false)
  }

  if (!facts) return null
  // No clean_game row yet: there is nothing to edit, and the panel used to
  // render nothing at all — so timings, directions and sides looked
  // impossible to set. Say why, and offer the two ways to create it.
  // Home/away and the half-1 direction live in L1, so they are settable
  // already.
  if (!facts.exists) {
    return (
      <GameFactsUnavailable gameId={gameId} onImport={onImport}
                            importing={importing}>
        <IdentityFacts identity={facts.identity} saving={saving}
                       save={save} />
        {msg && <p style={{ fontSize: '11px' }}>{msg}</p>}
      </GameFactsUnavailable>
    )
  }

  const p1 = facts.periods.find((p) => p.period_id === 1)
  const p2 = facts.periods.find((p) => p.period_id === 2)
  const suspicions = timingSuspicions(facts)
  const mismatches = recordMarkMismatches(facts)
  const fixes = timingFixes(facts)
  const fps = servedFps(facts.capture_fps)

  const secsToClock = (s: number | null) => {
    if (s == null) return '\u2014'
    const m = Math.floor(s / 60)
    const ss = Math.floor(s % 60)
    return `${m}:${ss.toString().padStart(2, '0')}`
  }

  return (
    <Panel title="Game Facts" built={true} defaultOpen={false}>
      <IdentityFacts identity={facts.identity} saving={saving} save={save} />
      <Team0Direction t0={facts.team0_half1}
                      legacyHomeIndex={facts.legacy_home_index}
                      legacyDir={facts.identity?.legacy_home_attack_dir_h1 ?? null} />
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                    gap: '4px 12px', fontSize: '12px', maxWidth: '560px' }}>
        <span className="t-label">Match date</span>
        <input className="adm-input" type="date"
               defaultValue={facts.match_date ?? ''}
               style={{ fontSize: '12px', padding: '2px 6px',
                        width: '150px' }}
               onChange={(e) => {
                 if (e.target.value
                     && e.target.value !== (facts.match_date ?? ''))
                   save({ match_date: e.target.value })
               }} />

        <span className="t-label">FPS</span>
        <input className="adm-input" type="number" step="0.01" min="1"
               defaultValue={facts.capture_fps ?? ''}
               style={{ fontSize: '12px', padding: '2px 6px', width: '90px' }}
               onBlur={(e) => {
                 const v = parseFloat(e.target.value)
                 if (!isNaN(v) && v !== facts.capture_fps)
                   save({ capture_fps: v })
               }} />

        <span className="t-label">Total frames</span>
        <input className="adm-input" type="number" step="1" min="0"
               defaultValue={facts.total_frames ?? ''}
               style={{ fontSize: '12px', padding: '2px 6px', width: '110px' }}
               onBlur={(e) => {
                 const v = parseInt(e.target.value)
                 if (!isNaN(v) && v !== facts.total_frames)
                   save({ total_frames: v })
               }} />

        <span className="t-label">Club side</span>
        {/* game_facts.org_side — what Matches files under Our / Opponent
            Matches. "Not our match" clears it; the stack keeps analysing
            the side it was (its own copy cannot be empty). */}
        <select className="adm-select" defaultValue={facts.club_side ?? ''}
                aria-label="Club side"
                title="Which side of this match belongs to the club — or neither"
                style={{ fontSize: '12px', width: '150px' }}
                onChange={(e) => save({ club_side: e.target.value || null })}>
          <option value="home">Home</option>
          <option value="away">Away</option>
          <option value="">Not our match</option>
        </select>

        <span className="t-label">Teams</span>
        <span>
          <strong>{facts.home_team ?? '?'}</strong>
          <span className="muted"> (home)</span>
          <span style={{ opacity: 0.5, margin: '0 6px' }}>vs</span>
          <strong>{facts.away_team ?? '?'}</strong>
          <span className="muted"> (away)</span>
        </span>

        <span className="t-label">Pitch size</span>
        <PitchSize pitch={facts.pitch} saving={saving} save={save} />
      </div>

      {/* Per-half timeline: frame + video timing, switchable direction */}
      <div style={{ marginTop: '10px' }}>
        <span className="t-label" style={{ display: 'block',
                borderBottom: '1px solid #333', paddingBottom: '2px',
                marginBottom: '6px' }}>
          Timeline
          <button className="adm-btn adm-btn--quiet"
                  style={{ marginLeft: '8px', fontSize: '11px', padding: '1px 6px' }}
                  title="Mark kick off, half-time and end on the video, in its own window"
                  onClick={() => openVideoTimes(gameId)}>
            Set match timings&hellip;
          </button>
        </span>
        <MatchClock key={facts.periods.map((p) => `${p.video_start_s}-${p.video_end_s}`).join('|')}
                    facts={facts} saving={saving} save={save} />
        {[p1, p2].map((p, i) => {
          const label = i === 0 ? '1H' : '2H'
          if (!p) {
            return (
              <NewPeriodRow key={label} label={label} periodId={i + 1}
                            facts={facts} saving={saving} save={save} />
            )
          }
          return (
            <div key={p.period_id}
                 style={{ display: 'flex', gap: '14px',
                          alignItems: 'center', flexWrap: 'wrap',
                          padding: '4px 0', fontSize: '12px' }}>
              {/* No per-half direction here: L3 measures each half and
                  every builder reads L3 (home attacks +x throughout).
                  The 1H direction above (kept in L1) is the override. */}
              <strong style={{ width: '24px' }}>{label}</strong>
              <span>
                <span className="t-label">start</span>{' '}
                <input className="adm-input" type="number" step="0.1"
                       defaultValue={p.video_start_s ?? ''}
                       style={{ fontSize: '11px', padding: '1px 4px',
                                width: '80px' }}
                       onBlur={(e) => {
                         const v = parseFloat(e.target.value)
                         if (!isNaN(v) && v !== p.video_start_s)
                           save({ periods: [{ period_id: p.period_id,
                                              video_start_s: v }] })
                       }} />
                <span className="muted" style={{ marginLeft: '4px',
                        fontSize: '10px' }}>
                  {secsToClock(p.video_start_s)}{' \u00b7 '}f{fmt(p.start_frame_idx)}
                </span>
              </span>
              <span>
                <span className="t-label">end</span>{' '}
                <input className="adm-input" type="number" step="0.1"
                       defaultValue={p.video_end_s ?? ''}
                       style={{ fontSize: '11px', padding: '1px 4px',
                                width: '80px' }}
                       onBlur={(e) => {
                         const v = parseFloat(e.target.value)
                         if (!isNaN(v) && v !== p.video_end_s)
                           save({ periods: [{ period_id: p.period_id,
                                              video_end_s: v }] })
                       }} />
                <span className="muted" style={{ marginLeft: '4px',
                        fontSize: '10px' }}>
                  {secsToClock(p.video_end_s)}{' \u00b7 '}f{fmt(p.end_frame_idx)}
                </span>
              </span>
              <span className="muted" style={{ fontSize: '10px' }}>
                {fps !== null && (p.end_frame_idx - p.start_frame_idx) > 0
                  ? `${(((p.end_frame_idx - p.start_frame_idx) / fps) / 60).toFixed(1)} min of frames`
                  : ''}
                {p.source === 'vendor_measured' &&
                  <span title={'Frame bounds measured from the tracking '
                               + 'itself — a time edit updates the video '
                               + 'mapping, never these frames.'}
                        style={{ marginLeft: '6px' }}>
                    · measured
                  </span>}
              </span>
            </div>
          )
        })}
      </div>

      {mismatches.length > 0 && (
        <div role="alert" style={{ marginTop: '8px', padding: '6px 10px',
                      background: 'rgba(234,179,8,0.08)',
                      border: '1px solid rgba(234,179,8,0.35)',
                      borderRadius: '4px', fontSize: '11px' }}>
          <strong style={{ color: '#eab308' }}>
            The game record disagrees with the Timeline:
          </strong>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {mismatches.map((r) => <li key={r}>{r}</li>)}
          </ul>
          <span className="muted">
            L2&rsquo;s frame-rate check and processing runs read the game
            record. Check the times, then press Set halves: it writes all
            four to both.
          </span>
        </div>
      )}

      {suspicions.length > 0 && (
        <div style={{ marginTop: '8px', padding: '6px 10px',
                      background: 'rgba(234,179,8,0.08)',
                      border: '1px solid rgba(234,179,8,0.35)',
                      borderRadius: '4px', fontSize: '11px' }}>
          <strong style={{ color: '#eab308' }}>Timings look suspect:</strong>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {suspicions.map((r) => <li key={r}>{r}</li>)}
          </ul>
          {fixes.lines.length > 0 && (
            <>
              <strong style={{ display: 'block', marginTop: '6px' }}>
                Suggested change (from the tracking data):
              </strong>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {fixes.lines.map((l) => <li key={l}>{l}</li>)}
              </ul>
              {fixes.body && (
                <div style={{ marginTop: '6px', display: 'flex', gap: '8px',
                              alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="adm-btn"
                          style={{ fontSize: '11px', padding: '1px 8px' }}
                          disabled={saving}
                          onClick={() => fixes.body && save(fixes.body)}>
                    Apply suggested frames
                  </button>
                  <span className="muted" style={{ fontSize: '10px' }}>
                    L4 and later were built with the old frames — rebuild
                    from L4 after applying.
                  </span>
                </div>
              )}
            </>
          )}
          {fixes.lines.length === 0 && (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Frame bounds already match the tracking data — check the
              video start/end times against the clips below.
            </p>
          )}
        </div>
      )}

      <div style={{ marginTop: '8px' }}>
        <span className="t-label is-clickable"
              role="button" tabIndex={0}
              onClick={() => setClipsOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setClipsOpen((o) => !o)
              }}>
          {clipsOpen ? '\u25be' : '\u25b8'} Timing Clips
        </span>
        {clipsOpen && (
          <TimingClips videoUrl={videoUrl} marks={CLIP_MARKS(facts)} />
        )}
      </div>

      {msg && <p style={{ fontSize: '11px', marginTop: '4px',
                          color: msg.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
        {msg}
      </p>}
      {saving && <p className="muted" style={{ fontSize: '11px' }}>Saving{'\u2026'}</p>}
    </Panel>
  )
}

type InputFile = {
  key: string
  relative: string
  bytes: number
  modified_at: number
  // .xml only: the vendor the file's first bytes say it is (the server's
  // ingest sniff), or null when they say neither
  sniffed_vendor?: 'dfl' | 'secondspectrum' | null
}

const VENDORS = ['tracab', 'secondspectrum', 'trdaas', 'dfl'] as const
// Vendors whose delivery is a positional file plus a metadata file.
const PAIRED: readonly Vendor[] = ['tracab', 'dfl']
type Vendor = typeof VENDORS[number]

// The current job's tracking, not loaded into the stack yet (input-files).
type NewTracking = { key: string; vendor: Vendor; job_id: string }
// The match video lives outside input/ of the folder holding the current
// tracking — a re-run's previous job, or an older layout (input-files).
type VideoCopyOffer = { job_id: string; folder: string; source_key: string }
type VideoCopy = { status: 'copied' | 'skipped' | 'partial' | 'in_place'
                   dest_key: string; size_bytes?: number; copied_bytes?: number
                   video_path?: string | null }

function TrackingFilePicker({ gameId, onIngested, onVideoChanged, canCopyVideo = false }: {
  gameId: number
  onIngested?: () => void
  /** The game's stored video moved (picked, or copied beside the tracking):
   *  the page's Video line and player reread it. */
  onVideoChanged?: () => void
  /** Whoever can build the stack may copy the match video into a new
   *  job's input/, beside its results/. */
  canCopyVideo?: boolean
}) {
  const [files, setFiles] = useState<InputFile[] | null>(null)
  const [videos, setVideos] = useState<InputFile[]>([])
  const [currentVideo, setCurrentVideo] = useState<string | null>(null)
  const [current, setCurrent] = useState<string | null>(null)
  const [fresh, setFresh] = useState<NewTracking | null>(null)
  const [videoCopy, setVideoCopy] = useState<VideoCopyOffer | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [copying, setCopying] = useState<string | null>(null)
  const [skipCopyFor, setSkipCopyFor] = useState<string | null>(null)
  const { log } = useActionLog()

  // Persist picker selections in localStorage so they survive tab
  // switches and page reloads.
  const storageKey = `layerstack-picker-${gameId}`
  function readSaved() {
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? JSON.parse(raw) as {
        vendor?: Vendor; positionalKey?: string; metadataKey?: string
      } : null
    } catch { return null }
  }
  const initSaved = useMemo(readSaved, [storageKey])

  const [vendor, setVendor] = useState<Vendor>(initSaved?.vendor ?? 'trdaas')
  const [positionalKey, setPositionalKey] = useState<string | null>(
    initSaved?.positionalKey ?? null)
  const [metadataKey, setMetadataKey] = useState<string | null>(
    initSaved?.metadataKey ?? null)

  // Save selections whenever they change
  useEffect(() => {
    try {
      const data: Record<string, string> = { vendor }
      if (positionalKey) data.positionalKey = positionalKey
      if (metadataKey) data.metadataKey = metadataKey
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch { /* quota or private browsing */ }
  }, [storageKey, vendor, positionalKey, metadataKey])

  useEffect(() => {
    setFiles(null); setCurrent(null); setFresh(null); setMsg(null)
    const saved = readSaved()
    api<{ files: InputFile[]; videos?: InputFile[]
          current_video_filename?: string | null
          current_tracking_source: string | null
          new_tracking?: NewTracking | null
          video_copy?: VideoCopyOffer | null }>(
      `/api/v1/opsqa/games/${gameId}/input-files`)
      .then((d) => {
        setFiles(d.files)
        setVideos(d.videos ?? [])
        setCurrentVideo(d.current_video_filename ?? null)
        setCurrent(d.current_tracking_source)
        setVideoCopy(d.video_copy ?? null)
        // A job the stack has not loaded yet wins over any saved pick:
        // the saved one names the previous job's file.
        if (d.new_tracking) {
          setFresh(d.new_tracking)
          setVendor(d.new_tracking.vendor)
          setPositionalKey(d.new_tracking.key)
          setMetadataKey(null)
          return
        }
        // Restore from localStorage; fall back to server's current source
        if (saved?.vendor) setVendor(saved.vendor)
        if (saved?.positionalKey) {
          setPositionalKey(saved.positionalKey)
        } else if (d.current_tracking_source) {
          setPositionalKey(d.current_tracking_source)
        }
        if (saved?.metadataKey) setMetadataKey(saved.metadataKey)
      })
      .catch(() => {})
  }, [gameId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!files || (files.length === 0 && videos.length === 0)) return null

  // Point the game's match video at a chosen R2 object (SKC@ORL on prod
  // carried another organization's legacy-layout video). Confirm first:
  // this swaps what every match page plays.
  const pickVideo = async (f: InputFile) => {
    const name = f.relative
    if (!window.confirm(`Use ${name} as this game's match video?`)) return
    setBusy(true); setMsg(null)
    try {
      await api(`/api/v1/opsqa/games/${gameId}/select-video`, {
        method: 'POST', body: JSON.stringify({ key: f.key }),
      })
      setCurrentVideo(f.key.split('/').pop() ?? null)
      setMsg(`Video set to ${name}`)
      log('done', `Match video repointed to ${name}`)
      onVideoChanged?.()
    } catch (e) {
      setMsg(`Error: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  // "Not now" on the copy offer holds for that job on this browser.
  const copyDismissKey = (job: string) => `layerstack-copy-video-no-${job}`
  const offerCopy = !!videoCopy && canCopyVideo && skipCopyFor !== videoCopy.job_id
    && (() => { try { return !localStorage.getItem(copyDismissKey(videoCopy.job_id)) }
                catch { return true } })()

  const declineCopy = () => {
    if (!videoCopy) return
    try { localStorage.setItem(copyDismissKey(videoCopy.job_id), '1') } catch { /* private */ }
    setSkipCopyFor(videoCopy.job_id)
  }

  // Server-side copy in R2; a big video lands across several requests
  // ("partial" = ask again, it resumes).
  const copyVideo = async () => {
    if (!videoCopy) return
    const job = videoCopy.job_id
    setCopying('Copying video\u2026'); setMsg(null)
    log('start', `Copy match video into job ${job.slice(0, 8)}…`)
    try {
      let r: VideoCopy
      for (let i = 0; ; i++) {
        r = await api<VideoCopy>(`/api/v1/ops/games/${gameId}/copy-video-to-job`, {
          method: 'POST', body: JSON.stringify({ job_id: job }),
        })
        if (r.status !== 'partial') break
        if (i >= 500) throw new Error('copy still unfinished after 500 requests')
        if (r.size_bytes) {
          setCopying(`Copying video\u2026 ${bytes(r.copied_bytes ?? 0)} of ${bytes(r.size_bytes)}`)
        }
      }
      const name = r.dest_key.split('/').slice(-3).join('/')
      const said = r.status === 'copied' ? `Video copied to ${name}`
        : `Video already in ${name}`
      setMsg(said)
      log('done', said)
      setVideoCopy(null)
      // the server pointed the game's video at the copy
      setCurrentVideo(r.dest_key.split('/').pop() ?? null)
      onVideoChanged?.()
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e)
      setMsg(`Error: ${m}`)
      log('fail', `Copy match video: FAILED — ${m}`)
    } finally {
      setCopying(null)
    }
  }

  const ingest = async () => {
    if (!positionalKey) { setMsg('Pick the tracking file first.'); return }
    if (PAIRED.includes(vendor) && !metadataKey) {
      setMsg(`${vendor} needs a metadata file as well.`); return
    }
    setBusy(true); setMsg(null)
    const t0 = Date.now()
    const name = positionalKey.split('/').pop()
    log('start', `Ingest tracking (${vendor}): ${name}…`)
    try {
      const body: Record<string, string | boolean> = {
        vendor, positional_key: positionalKey,
      }
      if (PAIRED.includes(vendor) && metadataKey) body.metadata_key = metadataKey
      const send = () => api(`/api/v1/opsqa/games/${gameId}/ingest-tracking`,
        { method: 'POST', body: JSON.stringify(body) })
      try {
        await send()
      } catch (e: unknown) {
        // A pod is still producing this game's tracking: the file picked
        // is the old one. Ingest only if the operator says so.
        if (!(e instanceof ApiError && e.code === 'tracking_in_flight')) throw e
        if (!window.confirm(`${e.message}.\n\nIngest the current file anyway?`)) {
          setMsg(`Not ingested — ${e.message}.`)
          log('info', `Ingest tracking: held — ${e.message}`)
          setBusy(false)
          return
        }
        body.force = true
        log('info', 'Ingest tracking: ingesting anyway while a pod is producing tracking')
        await send()
      }
      setCurrent(positionalKey)
      setFresh(null)
      setMsg(`Ingested as ${vendor}.`)
      log('done', `Ingest tracking: done as ${vendor} (${((Date.now() - t0) / 1000).toFixed(1)}s)`)
      onIngested?.()
    } catch (e: unknown) {
      const m = e instanceof ApiError ? e.message : String(e)
      setMsg(`Error: ${m}`)
      log('fail', `Ingest tracking: FAILED — ${m}`)
    }
    setBusy(false)
  }

  const row = (
    f: InputFile,
    selected: boolean,
    onPick: () => void,
  ) => (
    <div key={f.key}
         role="button" tabIndex={0}
         onClick={() => !busy && onPick()}
         onKeyDown={(e) => { if (e.key === 'Enter' && !busy) onPick() }}
         style={{
           display: 'flex', alignItems: 'center', gap: '8px',
           padding: '4px 8px', borderRadius: '4px', cursor: 'pointer',
           background: selected ? 'rgba(34,197,94,0.1)' : 'transparent',
           border: selected ? '1px solid rgba(34,197,94,0.3)'
                            : '1px solid transparent',
           marginBottom: '2px', fontSize: '11px',
           opacity: busy ? 0.5 : 1,
         }}>
      <span style={{ width: '14px', textAlign: 'center',
                     color: selected ? '#22c55e' : '#666' }}>
        {selected ? '\u25cf' : '\u25cb'}
      </span>
      <code style={{ flex: 1 }}>{f.relative}</code>
      <span className="t-data" style={{ minWidth: '60px' }}>
        {bytes(f.bytes)}
      </span>
      <span className="muted" style={{ minWidth: '50px' }}>
        {relativeTime(f.modified_at)}
      </span>
    </div>
  )

  return (
    <Panel title={`R2 Files (${files.length})`} built={true}
           defaultOpen={!current || !!fresh}>
      {fresh && (
        <p style={{ fontSize: '11px', marginBottom: '6px', color: '#f59e0b' }}>
          Job {fresh.job_id.slice(0, 8)} has tracking the stack has not
          loaded yet. It is selected below: Ingest tracking, then rebuild.
        </p>
      )}
      {(offerCopy || copying) && videoCopy && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
                      fontSize: '11px', marginBottom: '6px' }}>
          <span>
            The match video is in <code>{videoCopy.source_key.split('/').slice(-3, -1).join('/')}/</code>,
            not beside the current tracking. Copy {currentVideo ?? 'it'} to
            {' '}<code>{videoCopy.folder}/input/</code>?
          </span>
          <button className="adm-btn" style={{ fontSize: '11px', padding: '2px 10px' }}
                  disabled={!!copying} onClick={copyVideo}>
            {copying ?? 'Copy video'}
          </button>
          {!copying && (
            <button className="adm-btn" style={{ fontSize: '11px', padding: '2px 10px' }}
                    onClick={declineCopy}>
              Not now
            </button>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center',
                    marginBottom: '6px', fontSize: '11px' }}>
        <span className="t-label">Vendor</span>
        <select className="adm-select" value={vendor}
                style={{ fontSize: '11px' }}
                onChange={(e) => setVendor(e.target.value as Vendor)}>
          {VENDORS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button className="adm-btn"
                style={{ fontSize: '11px', padding: '2px 10px' }}
                disabled={busy}
                title="Run the real vendor L1 ingest: content-address the delivery, write the batch and entity rows"
                onClick={ingest}>
          {busy ? 'Ingesting\u2026' : 'Ingest tracking'}
        </button>
        {msg && (
          <span style={{ fontSize: '11px',
                         color: msg.startsWith('Error') ? '#ef4444'
                                                        : '#22c55e' }}>
            {msg}
          </span>
        )}
      </div>
      <p className="muted" style={{ fontSize: '11px', marginBottom: '4px' }}>
        Tracking file{current ? '' : ' (none ingested yet)'}:
      </p>
      {files.map((f) => row(f, positionalKey === f.key, () => {
        setPositionalKey(f.key)
        // The dropdown keeps the last choice, and a kept 'trdaas'
        // aimed a tracab .dat at the parquet reader (game 567,
        // 2026-10-20). An unambiguous extension picks the vendor; an .xml
        // is Second Spectrum's or DFL's, so it picks by the vendor the
        // listing sniffed from its first bytes; .json stays the operator's
        // call (TF10 positional and a Dender delivery are both .json). The
        // server sniffs the CONTENT at ingest and refuses a mismatch
        // either way, naming the vendor the file is.
        const v = f.key.endsWith('.dat') ? 'tracab'
          : f.key.endsWith('.parquet') ? 'trdaas'
            : f.key.endsWith('.xml') ? (f.sniffed_vendor ?? null) : null
        if (v) setVendor(v)
      }))}
      {PAIRED.includes(vendor) && (
        <>
          <p className="muted" style={{ fontSize: '11px',
                margin: '6px 0 4px' }}>
            Metadata file (a {vendor} delivery is positional + metadata):
          </p>
          {files.map((f) => row(f, metadataKey === f.key,
                                () => setMetadataKey(f.key)))}
        </>
      )}
      {videos.length > 0 && (
        <>
          <p className="muted" style={{ fontSize: '11px',
                margin: '8px 0 4px' }}>
            Match video (what every match page plays)
            {currentVideo ? <> — current: <code>{currentVideo}</code></> : ''}:
          </p>
          {videos.map((f) => row(
            f,
            !!currentVideo && f.key.endsWith(`/${currentVideo}`),
            () => void pickVideo(f),
          ))}
        </>
      )}
    </Panel>
  )
}

function MatchView({ gameId, settings, canCopyVideo = false }: {
  gameId: number
  /** Whoever can build the stack (staff, platform admins): offer to copy
   *  the match video into a new job's input/. */
  canCopyVideo?: boolean
  /** Staff get the Matches tab's settings gear on this game's strip. */
  settings?: { canUploadBadges: boolean; canDelete?: boolean
               onDeleted?: () => void }
}) {
  const [data, setData] = useState<LayersPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<WarningsPayload | null>(null)
  const [scanning, setScanning] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [timingsOpen, setTimingsOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState<string | null>(null)
  const { log, stackVersion } = useActionLog()
  // Full game-facts payload: drives the Game Facts editor AND the
  // header Import affordance (exists=false means no clean_game row).
  const [facts, setFacts] = useState<GameFacts | null>(null)
  // Bumped after an import so self-fetching children (summary card,
  // tracking picker) remount and refetch.
  const [refreshKey, setRefreshKey] = useState(0)
  // Chip statuses from the matches endpoint — drives panel border colors
  const [chipStatuses, setChipStatuses] = useState<Partial<Record<LayerKey, ChipStatus>>>({})
  // …and, for a layer that is not green, why (after the section title).
  const [chipReasons, setChipReasons] = useState<Partial<Record<LayerKey, string | null>>>({})

  // Chip statuses from the matches endpoint — panel border colors
  const loadChips = useCallback(() => {
    api<{ matches: MatchRow[] }>(`/api/v1/opsqa/matches?game_id=${gameId}`)
      .then((d) => {
        const row = d.matches[0]
        if (row) {
          const s: Partial<Record<LayerKey, ChipStatus>> = {}
          const why: Partial<Record<LayerKey, string | null>> = {}
          for (const k of ['l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'serve'] as LayerKey[]) {
            s[k] = row.layers[k].status
            why[k] = notGreenReason(row.layers[k])
          }
          setChipStatuses(s)
          setChipReasons(why)
        }
      })
      .catch(() => {})
  }, [gameId])

  const loadAll = useCallback(() => {
    setData(null); setWarnings(null); setScanError(null); setError(null)
    setMatchInfo(null); setVideoUrl(null); setImportMsg(null)
    api<LayersPayload>(`/api/v1/opsqa/games/${gameId}/layers`)
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
    api<MatchInfo>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
      .then(setMatchInfo)
      .catch(() => {})
    // The endpoint answers video_url/video_url_fallback (see
    // opsqa_game_video) — d.url was a field that never existed, which
    // rendered "No playable video" for games whose video is fine.
    api<{ video_url: string | null; video_url_fallback?: string | null }>(
      `/api/v1/opsqa/games/${gameId}/video`)
      .then((d) => setVideoUrl(d.video_url ?? d.video_url_fallback ?? null))
      .catch(() => {})
    api<GameFacts>(`/api/v1/opsqa/games/${gameId}/game-facts`)
      .then(setFacts)
      .catch(() => setFacts(null))
    loadChips()
  }, [gameId, loadChips])

  // After any successful action (ingest, build, …) refetch the layer
  // panels and their colors in place — no null-out, so open panels and
  // scroll position survive.
  useEffect(() => {
    if (stackVersion === 0) return
    api<LayersPayload>(`/api/v1/opsqa/games/${gameId}/layers`)
      .then(setData)
      .catch(() => {})
    loadChips()
    // A build (Entities) can create the clean_game row Game Facts edits.
    reloadFacts()
  }, [stackVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  // Lightweight reload for game-facts only — the Game Facts panel calls
  // this after a save so it refreshes without unmounting (loadAll nulls
  // every piece of state, which unmounts the panel and loses the "Saved"
  // message the user just saw).
  const reloadFacts = useCallback(() => {
    api<GameFacts>(`/api/v1/opsqa/games/${gameId}/game-facts`)
      .then(setFacts)
      .catch(() => setFacts(null))
  }, [gameId])

  // The stored video moved (picked, or copied beside the tracking): reread
  // the player's URL in place. The Video line already rereads — the
  // action's log('done') bumps stackVersion, which refetches layers.
  const reloadVideo = useCallback(() => {
    api<{ video_url: string | null; video_url_fallback?: string | null }>(
      `/api/v1/opsqa/games/${gameId}/video`)
      .then((d) => setVideoUrl(d.video_url ?? d.video_url_fallback ?? null))
      .catch(() => {})
  }, [gameId])

  useEffect(() => { loadAll() }, [loadAll])

  const doImport = async () => {
    setImporting(true); setImportMsg(null)
    log('start', 'Import to stack…')
    try {
      const r = await api<{ imported: boolean; written?: Record<string, number>; reason?: string }>(
        `/api/v1/opsqa/games/${gameId}/import-to-stack`,
        { method: 'POST' })
      if (r.imported) {
        setImportMsg(`Imported: ${Object.entries(r.written ?? {}).map(([k, v]) => `${k}=${v}`).join(', ')}`)
        log('done', `Import: ${Object.entries(r.written ?? {}).map(([k, v]) => `${k}=${v}`).join(', ')}`)
        loadAll()
        setRefreshKey((k) => k + 1) // remount summary card + picker
      } else {
        setImportMsg(r.reason ?? 'Already imported')
        log('info', `Import: ${r.reason ?? 'already imported'}`)
      }
    } catch (e: unknown) {
      const m = e instanceof ApiError ? e.message : String(e)
      setImportMsg(`Error: ${m}`)
      log('fail', `Import: FAILED — ${m}`)
    }
    setImporting(false)
  }

  const computeWarnings = useCallback(() => {
    setScanning(true); setScanError(null)
    api<WarningsPayload>(`/api/v1/opsqa/games/${gameId}/warnings`)
      .then((w) => { setWarnings(w); setScanning(false) })
      .catch((e: unknown) => {
        setScanError(e instanceof ApiError ? e.message : String(e))
        setScanning(false)
      })
  }, [gameId])

  // W35: the pinned summary card renders in every state, so the
  // layer colors are visible even while the panel payload loads.
  // Import renders BETWEEN the Timings chip and Build next, only while
  // the game has no clean_game row (founder instruction 2026-09-28).
  const importSlot = facts && !facts.exists ? (
    <span style={{ display: 'inline-flex', gap: '4px',
                   alignItems: 'center' }}>
      <button className="adm-btn"
              style={{ fontSize: '10px', padding: '1px 6px' }}
              disabled={importing}
              title="Create clean_game/team/period rows from the legacy tables"
              onClick={doImport}>
        {importing ? 'Importing…' : 'Import'}
      </button>
      {importMsg && (
        <span style={{ fontSize: '10px',
                       color: importMsg.startsWith('Error') ? '#ef4444' : '#22c55e' }}>
          {importMsg}
        </span>
      )}
    </span>
  ) : null
  // The per-game flags sit at the far left just above the layers strip
  // (Ben, 2026-10-22; first placed beside the tabs on 10-21), so they are
  // read with the game's L1–L6 chips and stay in its action log.
  const header = (
    <>
    <div className="ls-flags" data-testid="ls-flags">
      <ReviewToggle gameId={gameId} />
      <ReviewChecklist />
      <ServeToggle gameId={gameId} />
    </div>
    <StackSummaryCard gameId={gameId} key={refreshKey}
                      actionsSlot={importSlot}
                      settings={settings && {
                        ...settings,
                        // names, date and timings feed the matchup line
                        // and Game Facts below the strip
                        onSaved: () => {
                          api<MatchInfo>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
                            .then(setMatchInfo).catch(() => {})
                          reloadFacts()
                        },
                      }} />
    </>
  )
  if (error) return <>{header}<p className="error">{error}</p></>
  if (!data) return <>{header}<p className="muted">Loading…</p></>

  const { layers } = data
  return (
    <>
      {header}
      {/* the matchup line first: which game, which way, its timings and
          score, above the notes about it (Ben, 2026-10-26) */}
      {matchInfo && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
                      padding: '8px 12px', background: 'var(--color-surface-raised, #f8f8f8)',
                      borderRadius: '6px', flexWrap: 'wrap', fontSize: '12px',
                      marginBottom: 'var(--space-2)' }}>
          {/* The match itself, away at home, large enough to tell at a
              glance which game this is (Ben, 2026-10-21). */}
          <span className="ls-matchup" data-testid="ls-matchup">
            <span title="Away">{matchInfo.away_team ?? '?'}</span>
            <span className="ls-matchup__at">at</span>
            <span title="Home">{matchInfo.home_team ?? '?'}</span>
          </span>
          {matchInfo.home_attack_dir_h1 && (
            <span><span className="t-label">H1 dir</span>{' '}
              <code>{matchInfo.home_attack_dir_h1}</code>
            </span>
          )}
          {(matchInfo.periods ?? []).length > 0 && (() => {
            // match-lab-info's rate; with none a period shows its frames only
            const fps = servedFps(matchInfo.fps)
            const periodTime = (frames: number) => {
              if (fps === null) return null
              const m = Math.floor(frames / fps / 60)
              const s = Math.floor((frames / fps) % 60)
              return `${m}:${s.toString().padStart(2, '0')}`
            }
            return (
              <span style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span className="t-label is-clickable"
                      style={{ cursor: 'pointer' }}
                      role="button" tabIndex={0}
                      onClick={() => setTimingsOpen(!timingsOpen)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setTimingsOpen(!timingsOpen) }}>
                  {timingsOpen ? '▾' : '▸'} Timings
                </span>
                {matchInfo.periods.map((p) => (
                  <span key={p.period_id}>
                    <span style={{ fontWeight: 600 }}>
                      {p.period_id === 1 ? '1H' : p.period_id === 2 ? '2H' : `P${p.period_id}`}
                    </span>{' '}
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      f{fmt(p.start_frame_idx)}{'\u2013'}{fmt(p.end_frame_idx)}
                    </span>
                    {fps !== null && (
                      <span className="muted" style={{ marginLeft: '4px' }}>
                        ({periodTime(p.end_frame_idx - p.start_frame_idx)})
                      </span>
                    )}
                  </span>
                ))}
              </span>
            )
          })()}
          {timingsOpen && matchInfo && videoUrl && (() => {
            const marks: { label: string; time: number | null }[] = [
              { label: 'Kick Off', time: matchInfo.video_game_start_time },
              { label: 'End of First Half', time: matchInfo.video_halftime_start_time },
              { label: 'Second Half Kick Off', time: matchInfo.video_halftime_end_time },
              { label: 'End of Game', time: matchInfo.video_normaltime_end_time },
            ]
            // ONE clip renderer for both surfaces (header Timings and
            // the Game Facts panel) — sized 3x with the aspect ratio
            // respected (founder instruction, 2026-09-29).
            return <TimingClips videoUrl={videoUrl} marks={marks} />
          })()}
          {matchInfo.goals.length > 0 && (
            <span><span className="t-label">Score</span>{' '}
              <strong>
                {matchInfo.goals.filter(g => g.team_id === matchInfo.teams?.home).length}
                {'\u2013'}
                {matchInfo.goals.filter(g => g.team_id === matchInfo.teams?.away).length}
              </strong>
            </span>
          )}
        </div>
      )}
      <GameNotes gameId={gameId} />
      <TurnaroundPanel gameId={gameId} staff={!!settings} />
      <MatchQuality gameId={gameId} refreshKey={stackVersion} />
      <div style={{ fontSize: '11px', color: 'var(--color-text, #fff)',
                    lineHeight: 1.6, marginBottom: 'var(--space-3)' }}>
        <div>
          Object prefix: <code>{data.game_prefix ?? 'unknown'}</code>
          {data.parquet_note ? ` \u00b7 ${data.parquet_note}` : ''}
        </div>
        {data.video_path && <div>Video: <code>{data.video_path}</code></div>}
        {data.tracking_path && <div>Tracking: <code>{data.tracking_path}</code></div>}
      </div>
      {/* No key here: remounting the picker on refresh wiped the
          "Ingested as <vendor>" confirmation the operator just earned
          (SDET 74.0 low item). It refetches on gameId itself. */}
      <TrackingFilePicker gameId={gameId} canCopyVideo={canCopyVideo}
                          onVideoChanged={reloadVideo} />
      <GameFactsPanel gameId={gameId} facts={facts} videoUrl={videoUrl}
                      onSaved={reloadFacts} onImport={doImport}
                      importing={importing} />

      <ActionLogPanel />

      <Panel title="L1 — raw vendor delivery" built={layers.l1.built} defaultOpen={false}
             borderColor={chipStatuses.l1 ? CHIP_COLORS[chipStatuses.l1] : undefined}
             reason={chipReasons.l1}>
        {layers.l1.built ? (
          <>
            <table className="adm-table">
              <thead>
                <tr><th>Batch</th><th>Feed</th><th>Vendor</th>
                  <th>Status</th><th>sha256</th><th>Received (UTC)</th></tr>
              </thead>
              <tbody>
                {layers.l1.batches.map((b) => (
                  <tr key={b.ingest_batch_id}>
                    <td className="t-data">{b.ingest_batch_id}</td>
                    <td>{b.feed_type}</td>
                    <td>{b.vendor}</td>
                    <td>{b.status}</td>
                    <td className="t-data">
                      {b.content_sha256.slice(0, 12)}…</td>
                    <td className="t-data">{b.received_at ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {Object.keys(layers.l1.entity_rows).length > 0 && (
              <CountsTable counts={layers.l1.entity_rows} hideEmpty={chipStatuses.l1 === 'green'} />
            )}
          </>
        ) : null}
      </Panel>

      <Panel title="L2 — vendor-shaped clean parquet" built={layers.l2.built}
             borderColor={chipStatuses.l2 ? CHIP_COLORS[chipStatuses.l2] : undefined}
             reason={chipReasons.l2}>
        {layers.l2.built && <ParquetTables tables={layers.l2.tables} hideEmpty={chipStatuses.l2 === 'green'} />}
      </Panel>

      <Panel title="L3 — canonical space + kinematics" built={layers.l3.built}
             borderColor={chipStatuses.l3 ? CHIP_COLORS[chipStatuses.l3] : undefined}
             reason={chipReasons.l3}>
        {layers.l3.built && <ParquetTables tables={layers.l3.tables} hideEmpty={chipStatuses.l3 === 'green'} />}
      </Panel>

      <Panel title="L4 — spatial events" built={layers.l4.built}
             borderColor={chipStatuses.l4 ? CHIP_COLORS[chipStatuses.l4] : undefined}
             reason={chipReasons.l4}>
        {layers.l4.built && (
          <>
            <CountsTable counts={layers.l4.counts} hideEmpty={chipStatuses.l4 === 'green'} />
            {Object.keys(layers.l4.action_types).length > 0 && (
              <p className="muted">
                Action types: {Object.entries(layers.l4.action_types)
                  .map(([k, v]) => `${k} ${fmt(v)}`).join(' · ')}
              </p>
            )}
          </>
        )}
      </Panel>

      <Panel title="L5 — structure" built={layers.l5.built}
             borderColor={chipStatuses.l5 ? CHIP_COLORS[chipStatuses.l5] : undefined}
             reason={chipReasons.l5}>
        {layers.l5.built && <CountsTable counts={layers.l5.counts} hideEmpty={chipStatuses.l5 === 'green'} />}
      </Panel>

      <Panel title="L6 — derived signal" built={layers.l6.built}
             borderColor={chipStatuses.l6 ? CHIP_COLORS[chipStatuses.l6] : undefined}
             reason={chipReasons.l6}>
        {layers.l6.built
          ? <CountsTable counts={layers.l6.counts} hideEmpty={chipStatuses.l6 === 'green'} />
          : layers.l6.note ? <p className="muted">{layers.l6.note}</p> : null}
      </Panel>

      <Panel title="Serve overlays" built={layers.serve.built}
             borderColor={chipStatuses.serve ? CHIP_COLORS[chipStatuses.serve] : undefined}
             reason={chipReasons.serve}>
        {layers.serve.built && (
          <table className="adm-table">
            <thead><tr><th>Overlay</th><th>Rows</th><th>Size</th></tr></thead>
            <tbody>
              {Object.entries(layers.serve.overlays)
                .filter(([, o]) => chipStatuses.serve !== 'green' || o.built)
                .map(([name, o]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td className="t-data">{fmt(o.rows)}</td>
                  <td className="t-data">{bytes(o.bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <CorrectionsPanel gameId={gameId} />

      <Panel title="Persisted run summaries (meta_analysis_run)"
             built={data.analysis_runs.length > 0}>
        {data.analysis_runs.length === 0 ? (
          <p className="muted">
            No analysis runs recorded. Builder check summaries are not
            persisted anywhere else.
          </p>
        ) : (
          <table className="adm-table">
            <thead>
              <tr><th>Run</th><th>Analysis</th><th>Tier</th>
                <th>Current</th><th>Run at (UTC)</th>
                <th>Checks</th></tr>
            </thead>
            <tbody>
              {data.analysis_runs.map((r) => (
                <tr key={r.run_id}>
                  <td className="t-data">{r.run_id}</td>
                  <td>{r.analysis_id}</td>
                  <td>{r.tier}</td>
                  <td>{r.is_current ? 'yes' : 'no'}</td>
                  <td className="t-data">{r.run_at ?? '—'}</td>
                  <td className="t-data">
                    <ChecksCell tier={r.tier} checks={r.failed_checks} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <section style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="t-label">Warnings</h2>
        <p className="muted">
          Warning counters scan this game&apos;s local artifacts
          column-by-column, so they run only when asked.
        </p>
        <button className="adm-btn" onClick={computeWarnings}
                disabled={scanning}>
          {scanning ? 'Scanning…' : 'Compute warnings'}
        </button>
        {scanError && <p className="error">{scanError}</p>}
        {warnings && (
          <>
            {Object.entries(warnings.side_sources).map(([table, dist]) => (
              <p key={table} className="muted">
                {table} side_source: {Object.entries(dist)
                  .map(([k, v]) => `${k} ${fmt(v)}`).join(' · ')}
              </p>
            ))}
            {Object.keys(warnings.scans).length === 0 ? (
              <p className="muted">
                {warnings.scan_note ?? 'No local artifacts to scan.'}
              </p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr><th>Artifact</th><th>Counters</th></tr>
                </thead>
                <tbody>
                  {Object.entries(warnings.scans).map(([name, c]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      <td className="t-data">
                        {Object.entries(c)
                          .map(([k, v]) => `${k}=${fmt(v as number)}`)
                          .join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="muted">
              Not persisted by the stack (reported honestly, not as
              zero): {Object.entries(warnings.not_persisted)
                .map(([k, v]) => `${k} (${v})`).join('; ')}
            </p>
          </>
        )}
      </section>

      {/* W34-ADMIN-LAB: per-table stack summary (load on demand) */}
      <StackSummaryPanel gameId={gameId} />
    </>
  )
}

// ── Documentation view ──────────────────────────────────────────────

type DocEntry = {
  table: string
  title: string
  measures: string
  algorithm: string
}

const LAYER_DOCS: Record<string, { label: string; description: string; tables: DocEntry[] }> = {
  L1: {
    label: 'L1 — Raw Ingest',
    description: 'Vendor data ingested verbatim. Three adapters: Tracab (TF10 JSON), Second Spectrum (XML/JSON), trdaas (parquet). Units preserved as-is (cm for Tracab/trdaas, m for SS). One raw_ingest_batch row per file, raw_tracking_frame parquet per game.',
    tables: [{
      table: 'raw_tracking_frame',
      title: 'Raw Tracking Frame',
      measures: 'Per-frame vendor payload for every entity (player/ball/referee), verbatim JSON.',
      algorithm: 'Direct parse of vendor file into a uniform envelope (game_id, frame_seq, entity_type, payload_json). No transformation — vendor coordinates and units pass through unchanged.',
    }],
  },
  L2: {
    label: 'L2 — Vendor-Shaped Clean',
    description: 'Vendor data cleaned and split into typed tables per vendor (clean_tracab_*, clean_secondspectrum_*, clean_trdaas_*, clean_dfl_*). Units converted to meters (cm sources divided by 100). One set of tables per vendor format.',
    tables: [{
      table: 'clean_<vendor>_player_frame',
      title: 'Player Frame (per vendor)',
      measures: 'Per-player per-frame position, speed, jersey, and vendor-specific attributes.',
      algorithm: 'Parse payload_json per vendor schema. Tracab/trdaas: divide x,y,z by 100 to convert cm to meters. SS: already in meters, pass through. Speed derived from vendor field or inter-frame displacement.',
    }, {
      table: 'clean_<vendor>_ball_frame',
      title: 'Ball Frame (per vendor)',
      measures: 'Per-frame ball position (x, y, z in meters), speed, and possession/status flags.',
      algorithm: 'Same unit conversion as player frames. Ball z preserved for aerial analysis. Live/dead status from vendor flags where available.',
    }],
  },
  L3: {
    label: 'L3 — Canonical Space',
    description: 'Vendor-independent canonical coordinate system. Corner origin (0,0), x: 0..105m goal-to-goal, y: 0..68m touchline-to-touchline. Home attacks +x in period 1, flips in period 2. Frame indices are dense 0-based across periods.',
    tables: [{
      table: 'space_player_frame',
      title: 'Player Position (canonical)',
      measures: 'Per-player per-frame position in canonical meters, with true-metre twins preserving real pitch dimensions.',
      algorithm: 'Affine transform from vendor space to canonical [0,105]x[0,68]. Period-2 frames rotated 180 degrees so home always attacks +x. True-metre columns carry real pitch scale for spatial queries.',
    }, {
      table: 'space_ball_frame',
      title: 'Ball Position (canonical)',
      measures: 'Per-frame ball position in canonical meters with z preserved.',
      algorithm: 'Same affine transform as player frames. Ball z unchanged (already in meters from L2).',
    }, {
      table: 'space_player_kinematics',
      title: 'Player Kinematics',
      measures: 'Per-player per-frame velocity, acceleration, and distance covered.',
      algorithm: 'Backward-difference (bdiff1) derivatives at the feed\'s measured frame rate (tracab 29.97 Hz, secondspectrum 25 Hz, trdaas the video\'s, dfl the rate its delivery states). Speed = displacement/dt, acceleration = d(speed)/dt. Distance = cumulative sum of inter-frame displacement (per-game, reset each game). First frame of each tracking segment seeded with zero acceleration. Published only after B4 5-check gate passes.',
    }],
  },
  L4: {
    label: 'L4 — Events',
    description: 'Detected on-ball events from tracking data. All detectors read canonical L3 positions + promoted entities. No vendor knowledge.',
    tables: [{
      table: 'event_possession',
      title: 'Possession Spans',
      measures: 'Continuous possession spans — which team controls the ball, from when to when, with start/end locations.',
      algorithm: 'A team controls the ball when its nearest player is within 1.0 m (the action detector\'s touch). A ball no one is that close to (a pass in flight, a loose ball) changes nothing. The other team takes possession after 0.3 s of control without the team in possession touching it; its possession starts at its first touch. A dead ball or the period\'s end closes a possession after its last touch. Locations rotated into attacking perspective.',
    }, {
      table: 'event_action + event_pass',
      title: 'On-Ball Actions & Passes',
      measures: 'Individual on-ball actions (pass, carry, clearance, recovery, interception, tackle) with pass-specific attributes (distance, forward progress, receiver, where the ball arrived, opponents bypassed, whether it broke the last line).',
      algorithm: 'Within each possession, segment by nearest-to-ball player transitions. When the nearest player changes: same team = pass (if displacement > threshold) or carry; opponent team = tackle/interception. Pass attributes (distance, forward progress, receiver, reception point, opponents bypassed, beyond the second-last opponent) run from release (the action\'s end) to reception (the next action\'s start), in the team\'s attacking perspective; the action\'s own start→end is the passer\'s time on the ball. Category: progressive when the pass, release to reception, goes within 60 degrees of the attacking direction, or at 60-90 degrees past at least one opponent, with no minimum length; else supportive. Restart type classified from ball position at post-gap possession starts.',
    }, {
      table: 'event_shot + event_goal',
      title: 'Shots & Goals',
      measures: 'Shot events with origin, outcome (saved/blocked/off-target/goal), ball speed, and entry coordinates.',
      algorithm: 'Live-to-dead transition detection. Backtrack 60 frames for shot origin via velocity direction change (>45 degrees angular shift) or speed dip. Goal verified by ball crossing goal line within frame, under crossbar (z <= 2.44m).',
    }, {
      table: 'event_carry',
      title: 'Ball Carries',
      measures: 'Every spell of a player on the ball by the carry rule (Octavian\'s, PR #550): its touches, how far the ball and the carrier went, the nearest opponent, and whether it counts as a carry (meets_carry_rule, with the reason when not). A dribble that ends in a pass is a carry here and a pass in event_action.',
      algorithm: 'Holder spans start at possession markers: the tracking provider\'s own touch markers (Tracab), the CV feed\'s nearest-player state read from each run\'s first frame (TRDaaS, provisional), or the nearest player within 3 m confirmed by the provider\'s possession team (Second Spectrum, provisional). Each span is cut back to the release (ball more than 3 m from the carrier for 1 s); touches come from ball-speed rises and push-and-catch. A carry has at least 2 touches and the ball moved more than 1.5 m or an opponent came within 2.5 m. Category: progressive when the carry, start to end, goes within 60 degrees of the attacking direction, or at 60-90 degrees with a net gain of at least one opponent, with no minimum; else supportive. Ball speed is the provider\'s own wherever the rule\'s speed check finds it in m/s at the game\'s measured frame rate (Tracab, Second Spectrum); otherwise the L3 step times that rate (TRDaaS stores cm/s). With Tracab\'s own speed the stack reproduces the reference\'s carries exactly (KNOWLEDGE.md section 26).',
    }, {
      table: 'event_game_state',
      title: 'Game State',
      measures: 'State transitions: kickoff, halftime, fulltime, goals. Cumulative score and player counts per frame.',
      algorithm: 'Player-count heuristics for kick-off detection (22 players in formation). Goal events trigger score increments. Generated columns compute goal_diff, score_state, manpower_diff.',
    }],
  },
  L5: {
    label: 'L5 — Structure',
    description: 'Team spatial structure derived from player positions. Shape, formation templates, proximity rankings, shape-graph positions, and set-piece setups. Written as parquet (l5/store.py).',
    tables: [{
      table: 'structure_team_shape_frame',
      title: 'Team Shape',
      measures: 'Per-frame team envelope: convex hull area, length (x-span), width (y-span), centroid, and extreme players.',
      algorithm: 'Live-ball frames only. Exclude goalkeeper from hull. Convex hull on outfield positions in team\'s attacking perspective. Extremes via rotated coordinates. Hull by Andrew\'s monotone chain and its area by the shoelace formula, both in app/utils/football/team_shape.py.',
    }, {
      table: 'structure_team_formation + structure_formation_slot + structure_player_line_frame',
      title: 'Formation Detection',
      measures: 'Formation windows (5-second sustained plays) fitting standard templates, with per-frame player-to-line assignments.',
      algorithm: 'v0.2: gap-cluster outfield players by x-position into lines (dynamic gap threshold, max 6 lines). Template-fit against standard formations via cost minimization. Stable windows (minimum 150 frames ~5s) assigned a formation code. Per-slot track_id + jersey in structure_formation_slot; each player\'s line per frame in structure_player_line_frame. Coverage: 97–99% of in-play frames on tested games.',
    }, {
      table: 'structure_player_ball_proximity',
      title: 'Ball Proximity Rankings',
      measures: 'Per-player distance to ball and rankings (1-11 within team, 1-22 overall). Ball-carrier flag for rank-1 on possessing team.',
      algorithm: 'Live-ball frames. 2D Euclidean distance (+ 3D if z available) from all on-pitch players to ball. Rank by distance with track_id tie-break. Top 11 per team retained.',
    }, {
      table: 'structure_shape_graph_frame',
      title: 'Shape Graph Positions',
      measures: 'Every outfield player\u2019s relative position on in-play frames: forward level and lateral level, 1\u20135 each, and the 5 \u00d7 5 template label (LB, RCM, CF\u2026).',
      algorithm: 'Brandes et al. (2025). Per team, the ten outfield players (keeper excluded: flagged, else the deepest track per half) turned to attack +x; the Delaunay triangulation is thinned to the shape graph (45\u00b0 angular stability), and face centres split players into five levels per axis in two tiers. Sampled ~5 times a second; a team-frame without exactly ten outfielders is dropped and counted. Levels are relative to team-mates. Lateral 5 = the attacker\u2019s left by the canonical convention (not verified against video).',
    }, {
      table: 'structure_set_piece_setup',
      title: 'Set-Piece Setup',
      measures: 'Two rows per restart (kick-off, throw-in, goal kick, corner, free kick, penalty): the attacking and the defending team\u2019s setup \u2014 players in the penalty area and the goal area at the goal being attacked, short options (attackers within 15 m of the taker), and the defenders\u2019 marking scheme (zonal, man or mixed).',
      algorithm: 'Restarts are L4 actions with a restart_type. Positions are read from L3 at the restart frame, or the nearest frame with players within ~0.5 s. Boxes on the canonical 105 \u00d7 68 pitch. Marking from defender-to-attacker distances in the box. A game with no restarts writes the table empty.',
    }],
  },
  L6: {
    label: 'L6 — Signals',
    description: 'Advanced analytics: value models, spatial control, pressing events, progressive runs. All are model-versioned via meta_model_version + meta_analysis_run.',
    tables: [{
      table: 'signal_spatial_control_frame',
      title: 'Spatial Control Grid',
      measures: 'Per-frame pitch control probability on a 105x68 grid. Home control share, and each team\'s controlled cells summed by their xD for that team.',
      algorithm: 'Bounded arrival-time race (v0.1): per cell, each player\'s arrival t_i = t_r + ||cell - (pos + vel*t_r)||/v_max where t_r = 0.7s reaction, v_max = 5.0 m/s. Home probability p = 1/(1 + exp(-k*(min_t_away - min_t_home))) with k = 4.0. 1 m² grid (105×68 = 7,140 cells), stored as little-endian float16 (14,280 bytes/frame). Numpy-vectorized, no per-cell loop.',
    }, {
      table: 'signal_action_value',
      title: 'Action Value (xD)',
      measures: 'Per-action value: Expected Dangerosity (xD) at start and end. Opponent goal-side count and shape-zone classification. Served beside them: AE (attacking effectiveness) and the tactical score at the action\'s start and end, from the frame attack value (serve-enrich).',
      algorithm: 'Attack expectancy v0.2, stored and not served: logistic in distance to goal center, d0=30m center-line-exact rescale. Attack expectancy and territory per-action start/end values computed in attacking perspective via rotate_to_attacking; xD is the 2 m tile xD on the game\'s pitch (app/utils/football/xd.py), each position taken back to the venue through l6/game_xd.py. Shape zone: nine codes, the ball against the opponent\'s outfield block turned into the acting team\'s direction: IF/IFL/IFR in front of the block (between their most advanced player and the goal the attacker defends), IN/WL/WR level with the block, B/WLB/WRB behind it (between their deepest player and the goal the attacker attacks); L/R wider than their widest player, facing the attacked goal. Without L5 shape, thirds of the pitch length (own third IF, attacking third B) and width. Opponent goal-side count and lines ahead from the L3 positions and the L5 lines (l6/opponents.py), at the action\'s first and last frame; both are empty where the opponent team has more than 11 tracks on the frame, and so are the values read from every player there: a pass\'s difficulty on its release frame, a carry\'s on-ball pressure on its first frame, and the quality built on either, or on a shot\'s first frame.',
    }, {
      table: 'signal_possession_value',
      title: 'Possession Value',
      measures: 'Per-possession start/end/max/mean AE, TS, xD. Shape-cell dwell time and box entry counts.',
      algorithm: 'Aggregate action-level values over the possession span. Track max/mean of AE/TS/xD. Count box entries from actions crossing the 16.5m line.',
    }, {
      table: 'signal_press_event',
      title: 'Pressing Events',
      measures: 'Pressing sequences: who pressed, where, duration, peak/mean pressure, and outcome (regain/foul_won/forced_long/bypassed).',
      algorithm: 'Per-possession, identify defenders within PRESS_RADIUS_M (10m) of the ball. Closing speed = distance-delta / real-frame-gap (B5-corrected). Press event starts when closing speed >= 3.0 m/s, ends on gap tolerance (5 frames). Outcome: regain / foul_won / forced_long / bypassed, classified by what follows. Counterpress detection from turnovers.',
    }, {
      table: 'signal_run',
      title: 'Progressive & Supportive Runs',
      measures: 'Off-ball runs: start position and furthest point, distance, direction, line-position change, AE/TS/xD gained, and what stopped the run (stop_reason).',
      algorithm: 'Detection is run_rule, a port of the runs-v5 detector. Every 30 frames, each outfield player of the team the vendor names as owning the ball (home when it names none) may start a run, one at a time per player and type; flagged keepers are excluded by identity, and a team with no keeper flagged keeps its keeper among the candidates. A start is refused when the ball is not alive or was dead within the last 1 s, outside a phase, when the player has the ball, or on a step faster than 12 m/s. The progressive tracker follows the attacking direction, the supportive one the run\'s own direction (set once the player is 2 m from the start). Each stops at the first of: a reversal, the ball going dead, the other team owning the ball, a reception, more than 2 missing frames, a step faster than 12 m/s (a track switch), or 120 s of tracking; stop_reason records which. A run ends at its furthest point and is kept when that point is at least 5 m from the start in the vendor\'s own metres (progressive also 5 frames) and the run is its tracker\'s own type: PRO-R when start to furthest point is within 60 degrees of the attacking direction, or 60-90 degrees with a net gain of at least one opponent; else SUP-R. Inputs are the vendor\'s own where the feed has them (positions in metres, ball status, ball owner, frame rate), else the stack\'s, flagged in the build summary. Ball relation (forward/even/behind, ±1.0 m even band) and AE/TS/xD gain are measured from the start to the furthest point in attacking perspective. Stored positions and distances are on the 105 \u00d7 68 pitch (x_m / y_m), so a stored start-to-end distance can be under 5 m. A game missing an input the detector reads (the vendor\'s owner labels, the feed\'s own holder reading, the tracab frame rate) gets no runs: the step succeeds, stores none, and its build summary says why (missing_input, no_runs_because). A game with a keeper left in gets runs: its build summary names the team (keepers_not_excluded), and the export, the serve API, the ops pages, Match Video and the segment copilot give the note.',
    }, {
      table: 'signal_chance',
      title: 'Chances (xG)',
      measures: 'Chances: every shot, and the passes and carries that create one (box_entry, cross, cut_back, through_ball), each with expected goals, the player, the target and play type (open_play/set_piece).',
      algorithm: 'xG = sigmoid(-(a*distance_to_goal + b*angle_deficit)), logistic model on Euclidean distance to goal center and the goal angle subtended from shot position (7.32m goal width). Play type: open_play or set_piece from the possession\'s is_set_piece_origin flag. Creations (v0.2): box_entry = a completed pass or a carry ending in the box from outside it; cross = a pass, any outcome, released wide of the box at x ≥ 70 that ends in it; cut_back = a completed pass released at x ≥ 99, back ≥ 1 m, received in the box; through_ball = a completed pass received beyond the second-last opponent at release. One row per type, at the release frame and where the ball arrived; its xG is a shot\'s from that point.',
    }, {
      table: 'signal_defensive_involvement',
      title: 'Defensive Involvement',
      measures: 'Per defender per opponent possession: the most direct involvement reached (involvement_level) and how it ended.',
      algorithm: 'Ladder per frame, highest rung kept per possession: cover (>20 m from ball), track (≤20 m), close_down (≤10 m), press (≤10 m and closing ≥3 m/s), duel (≤1 m from an opponent, moving toward the ball), interception_attempt (≤1 m from the ball), regain (his action opened his team\'s next possession). Linked to the press event he took part in.',
    }, {
      table: 'signal_player_space_frame',
      title: 'Player Space Partition',
      measures: 'Per-player per-frame controlled area (m²) and control share (the player\u2019s share of the team\u2019s space), partitioned from the spatial-control grid. Keepers own the largest areas (median ~1,340 m² against ~130 m² outfield, game 15).',
      algorithm: 'Arrival-race params read from the spatial-control model row. Each grid cell assigned to the nearest player by arrival time; player areas sum to team area (partition invariant enforced, 0 violations on match 15). 4M+ rows per game.',
    }, {
      table: 'signal_frame_attack_value',
      title: 'Frame Attack Value',
      measures: 'Per-frame attacking value for the team in possession: xd, local/deep control from the grid, progression, tactical_score, attacking effectiveness (AE). Served as xd, control_near_ball, control_in_final_third, forward_speed_mps (a speed toward goal over the last 1 s), tactical_score, ball_depth_in_block, attacking_effectiveness (AE) and chance_creation_index (CCI).',
      algorithm: 'xd is the ball\'s 2 m tile xD on the game\'s pitch (l6/game_xd.py); attack expectancy (ae_score, v0.2) is imported from action_value. Local control = team probability within 10m of ball from the spatial-control grid. Deep control = team probability in the deep third (>70m). Progression from possession displacement. Lines ahead = the opponent’s outfield lines on that frame (L5 per-frame lines, the rule action value and runs use) whose band (deepest to highest player, 2 m either side of a one-player line) reaches the ball or lies nearer their goal; tactical_score = the legacy xD field at the ball × the legacy line multiplier for the lines it is past. AE is the shared attacking effectiveness of the frame (signal_frame_ae), capped at 1, on a row whose team is the one the frame is scored for by the vendor\'s ball owner, or by the stack\'s possession where the vendor names none; it is empty on the other team\'s rows, where neither names an owner, where a team has no tracked player, and where the game\'s AE could not be built. The build summary counts each reason. CCI = legacy calculate_cci over the ball carrier (nearest possessing player within 2 m; none → empty). Weights on meta_model_version. The opponents on a frame come from l6/opponents.py: where the opponent team has more than 11 tracks, the goal-side count, lines ahead, tactical_score, ball depth, CCI, IxG and AE are empty.',
    }, {
      table: 'signal_frame_ae',
      title: 'Frame Attacking Effectiveness',
      measures: 'The shared attacking effectiveness (AE) on every frame with a ball, for the team on the ball, with its parts: the ball\'s field value (field_xd), the team\'s control in the hull of its five nearest outfielders (hull_control), its share of the field-weighted control behind the defending line 1 (behind_line_control) and the ball\'s depth into the block (block_depth); owner_from_stack, owner_empty and ball_alive say how the frame\'s owner and ball are read.',
      algorithm: 'The research AE ported unchanged (app/utils/football/attacking_effectiveness.py): AE = field_xd × (hull_control + 0.5 behind_line_control + 0.3 block_depth), uncapped, on the game\'s own pitch. Positions are the vendor\'s metres from L3, unturned; home attacks +x where L3 is not rotated. The team is the vendor\'s ball owner; where the vendor names none, the team of the stack\'s possession (owner_from_stack); where neither does, the vendor\'s home side with no control, AE = 0.75 × field_xd (owner_empty). Dead-ball frames are scored and marked. AE is empty, with the reason, where the opponent team has more than 11 tracks or either team has no tracked player, and on every frame of a game whose owner labels are refused, whose pitch is undeclared, or whose pitch is not the one L3 is built on.',
    }, {
      table: 'signal_frame_pressing',
      title: 'Frame Pressing Score',
      measures: 'The research pressing score on every live frame with an owner, on the score\'s own scale, for the team pressing the team on the ball, with its parts: the pressing team\'s control within the model\'s radius of the ball (proximal_control), the field value of the cells there it controls (proximal_xd), their product (combined_press_value) and the ball team\'s control inside the hull of its players nearest the ball (attacking_network_control); in_buildout marks the frames inside the model\'s build-outs; owner_from_stack, owner_empty and ball_alive say how the owner and the ball are read. The radius and the players counted are PRESSING_RADIUS_M and NETWORK_PLAYERS in app/utils/football/pressing_score.py.',
      algorithm: 'The research model ported unchanged (app/utils/football/pressing_score.py): a weighted sum of proximal_component and network_component, clipped to the score\'s scale and rounded, on the game\'s own pitch; the weights and the decimals are W_PROXIMAL, W_NETWORK and SCORE_DECIMALS in app/utils/football/pressing_score.py. Every live frame with an owner is scored: the vendor\'s owner, else the team of the stack\'s possession (owner_from_stack). The score is empty, with the reason, on a dead ball, with no owner, where the pressing team has more tracks than a side fields players (MAX_PLAYERS_PER_TEAM in app/layerstack/l6/opponents.py) or either team has no tracked player, and on every frame of a game whose owner labels are refused, whose pitch is undeclared, or whose pitch is not the one L3 is built on.',
    }, {
      table: 'signal_press_moment',
      title: 'Press Moments',
      measures: 'Every press: the pressing team, its first, last and peak frames and its peak score.',
      algorithm: 'A run of consecutive frames on which one team\'s pressing score is at least the press threshold for at least the shortest press, starting at least the shortest gap after the previous press ends, ended by a turnover or a frame with no score; scored by its peak. The three values are PRESS_THRESHOLD, PRESS_MIN_S and PRESS_COOLDOWN_S in app/utils/football/pressing_score.py. The match page\'s press cards read these (press_moments).',
    }, {
      table: 'signal_run_ae',
      title: 'Run Attacking Effectiveness',
      measures: 'Every run of signal_run: the shared attacking effectiveness for the runner\'s team on the run\'s start frame as tracked (ae_start_factual), with only the runner moved to its position on the run\'s peak frame (ae_peak_cf_runner_only), and the difference (ae_delta_cf_runner_only). Not signal_run\'s ae_start, ae_end and ae_delta, which are the distance-based value at the runner\'s own positions.',
      algorithm: 'The research runs model\'s counterfactual, ported with two deliberate differences, on the shared AE (app/utils/football/attacking_effectiveness.py) and the frame AE step\'s inputs: one frame, the run\'s start, scored twice for the runner\'s team attacking its own way; the ball and every other player stay in place. The values are empty, with the reason, where the start frame has no ball inside a phase, or no player with a position (no_tracking), or the runner has no position on the start or the peak frame (runner_absent). The first difference: the players are read in the frame AE step\'s order, so ae_start_factual is that step\'s AE on the frame wherever it scores the runner\'s team as the team on the ball. The second: the frame AE step\'s input rules and range, which the research model does not apply, also leave a run empty where the opponent team has more tracks than a side fields players (MAX_PLAYERS_PER_TEAM in app/layerstack/l6/opponents.py) or either team has no tracked player, where either value is outside the bound (AE_CEILING in app/layerstack/l6/frame_ae.py), and on every run of a game whose pitch is undeclared or not the one L3 is built on.',
    }, {
      table: 'signal_pressure_frame',
      title: 'Frame Pressure',
      measures: 'Per-frame pressing index, pressers count, proximal control/xd, and combined pressure value for the defending team.',
      algorithm: 'pressers_count = defenders within 10m. pressing_index = distance-decay sum of closing speeds (B5-corrected real-frame-gap). proximal_control from the spatial-control grid at ball position. combined = weighted sum (w_intensity 0.4, w_control 0.3, w_xd 0.3, index_scale 2.0).',
    }, {
      table: 'signal_pass_option_frame, signal_pass_value',
      title: 'Pass Options',
      measures: 'Per-frame pass options for the ball carrier: one row per team-mate with distance, lane_control (the passing team\'s control along the line, 0 to 1), the change in xD, tactical score and attacking effectiveness a pass there would make, and its pass difficulty (0 to 1). Also writes signal_pass_value: the same three changes for every pass played, release to reception.',
      algorithm: 'v0.2. Passer = nearest possessing-team player to ball. Options = all other on-pitch teammates. Each change = value at the option (or reception) − value at the ball (or release), on attack_value\'s scale (spot_values.FrameSpots). Pass difficulty = the played-pass model (score_pass) from the ball to the option, the frame\'s control surface shared.',
    }, {
      table: 'signal_pass_progression',
      title: 'Pass Progression',
      measures: 'Per completed pass: the Analytics progression score (0–1) and its five parts — reception-point field value, opponents bypassed, opponent lines bypassed, depth through the lines, and the passing team\'s control at the reception point.',
      algorithm: 'Analytics\' own scoring code (utils/football/pass_score.py): score = 0.15 location_xd + 0.20 players bypassed/10 + 0.30 lines bypassed/lines + 0.25 zone position + 0.10 spatial control. Opponents at release from L3, turned into the passer\'s attacking frame; lines are Analytics\' subgroups of those opponents; control from the spatial-control grid at reception. Category (progressive/supportive) is L4\'s. The opponents at release come from l6/opponents.py: where the opponent team has more than 11 tracks on the release frame, the score and the parts read from the opponents (players and lines bypassed, their scores, zone position) are empty, and Match Video lists the pass with no score.',
    }],
  },
  Serve: {
    label: 'Serve — Consumer Views',
    description: 'What the UI, Insights Lab, the assistant and match video read — the ONLY tables consumer surfaces should read. Every serve table is listed with its columns, under the names readers use, in the data dictionary below (views.SERVE_CATALOG, serve/columns.py).',
    tables: [{
      table: 'l3/* \u00b7 l5/* \u00b7 l6/* (served in place)',
      title: 'Stack Tables Under Serve Names',
      measures: 'Frames, structure and L6 frame tables (ball and player positions, shape, formations, proximity, shape graph, set pieces, spatial control, pass options and progression, attack value, pressure, player space), read from their own layer parquets.',
      algorithm: 'No copy: a view over the layer parquet that renames cryptic columns to plain ones (xd_delta \u2192 xd_change, frame_idx \u2192 frame, x_att_m \u2192 x_attacking_m \u2026). Columns without a rename keep their name.',
    }, {
      table: 'serve/events/*',
      title: 'Event & Signal Parquets',
      measures: 'L4 events (possessions, actions, passes, carries, shots, goals, game state) and the L6 possession value, action value and chances.',
      algorithm: 'serve-event-parquets exports them from the stack\u2019s Postgres tables, one parquet each, filtered to the game.',
    }, {
      table: 'serve/game_summary \u00b7 identity_overlay \u00b7 quality_overlay',
      title: 'Match Metadata Overlays',
      measures: 'Game info and counts, track \u2192 team / jersey / player name, and the data-quality row.',
      algorithm: 'serve-overlays (game summary, identity) and serve-quality (the data-quality row, graded after every other step of the build): tens of rows each, from clean_game, clean_game_player and the tracking delivery.',
    }, {
      table: 'serve/summaries/*',
      title: 'Match Summaries',
      measures: 'team_pressing_timeline and team_threat_timeline (each team per 5 match minutes, broadcast clock: seconds without / with the ball, average pressure, share with 2+ pressers, pressing in the opponent half, ball wins; xD and AE (attacking effectiveness), seconds in the final third) and player_space_summary (each player by half, team on the ball or not, and pitch third: seconds, average controlled area and share of team space; the keeper marked).',
      algorithm: 'serve-summaries from the L6 frame tables. Live ball only; possessions own frames half-open [start, end). A ball win is a takeover kept with a completed pass. Final third: the last third of the pitch the game\u2019s positions are placed on. Player space sampled ~5 times a second. Keeper: the flagged track, else the team\u2019s most often deepest.',
    }, {
      table: 'serve/tables/<stamp>/*',
      title: 'Enriched Copies',
      measures: 'Every serve table again with the context readers otherwise join for: team id / index / name, player, period, match clock and video second. Actions, passes, carries and shots add the pressure on the team at the action\u2019s first frame; possessions add their highest xD, AE (attacking effectiveness), tactical score and final-third control, and the pressure they faced; action_value adds the action\u2019s AE and tactical score at its start and end.',
      algorithm: 'serve-enrich, last in every build. The stamp fingerprints the game\u2019s database facts and every source\u2019s modification time: readers use the copy only while its stamp is current, else the raw table, and Ops/QA shows the step pending again. Row counts must match the sources.',
    }, {
      table: 'serve/tracking_frames.parquet',
      title: 'Match Video Tracking',
      measures: 'The match-video tracking file in the legacy loader\u2019s flattened schema (frame, player / ball, jersey, team, x / y / z, speed).',
      algorithm: 'serve-tracking from L3 and the identity overlay: camera-view centred centimetres, as the legacy file, in 9,000-frame row groups so the player seeks without Flask.',
    }, {
      table: 'serve/pass_potential.json.gz',
      title: 'Pass Potential',
      measures: 'The pass-potential response: each sampled in-possession frame, its ball holder and every pass option with distance, angle, forward progress and score.',
      algorithm: 'serve-pass-potential runs the endpoint\u2019s own builder once at stack build and publishes the body gzipped. The endpoint serves it only while it is newer than every table it was built from and was built with the game\u2019s current home index; otherwise it builds live (25\u201335 s on a full match).',
    }, {
      table: 'serve/manifest/*',
      title: 'Serve Manifest',
      measures: 'One small JSON per game listing every serve table it has, with columns, row count and location.',
      algorithm: 'Rewritten after every successful step (create-only, newest wins), so listing a game\u2019s tables is one read instead of a DESCRIBE per parquet.',
    }],
  },
  'Promote': {
    label: 'L3 Entities — vendor → canonical players, teams, periods',
    description: 'Bridges vendor-specific roster/period tables into uniform canonical tables. After promote, no builder reads vendor tables directly. This is the last place vendor-specific track_id derivation logic lives.',
    tables: [{
      table: 'clean_game',
      title: 'Game Metadata',
      measures: 'Pitch dimensions, capture FPS, total frames, org side, timing/identity source.',
      algorithm: 'Read vendor ingest metadata (pitch from tracab/trdaas/dfl metadata, standard 105x68 fallback for SS). Derive total frames from period bounds. FPS from game_facts or vendor default.',
    }, {
      table: 'clean_game_player',
      title: 'Player-to-Team Mapping',
      measures: 'Maps track_id to team_id and jersey number for each game.',
      algorithm: 'Vendor-specific track_id derivation: tracab=vendor_player_id, SS and dfl=roster_id, trdaas=team_code*100000+player_id. Written once, read by every L4+ builder.',
    }, {
      table: 'clean_match_period',
      title: 'Period Bounds',
      measures: 'Start/end frame indices per period with dense 0-based indexing and home-attacks-positive-x flag.',
      algorithm: 'Read vendor period table, deduplicate measured vs vendor_declared rows (prefer measured), compute dense frame_idx cumulative across periods.',
    }],
  },
}

function DocsView() {
  const [expanded, setExpanded] = useState<string | null>(null)
  const toggle = (key: string) => setExpanded(expanded === key ? null : key)

  return (
    <>
      <p className="page-sub" style={{ marginTop: 0 }}>
        Algorithm documentation for each layer in the Priori stack.
        Each table describes what it measures and how it is computed; the
        serve tables&rsquo; columns are in the <a href="#data-dictionary">data
        dictionary</a> below.
      </p>
      {Object.entries(LAYER_DOCS).map(([key, layer]) => (
        <div key={key} style={{ marginBottom: 'var(--space-3)' }}>
          <h3 style={{ cursor: 'pointer', margin: '8px 0 4px',
                       fontSize: '15px', fontWeight: 600 }}
              onClick={() => toggle(key)}>
            {expanded === key ? '\u25BC' : '\u25B6'} {layer.label}
          </h3>
          <p className="muted" style={{ fontSize: '12px', margin: '0 0 8px 20px' }}>
            {layer.description}
          </p>
          {expanded === key && (
            <div style={{ marginLeft: 20 }}>
              {layer.tables.map((t) => (
                <div key={t.table} style={{
                  background: 'var(--color-surface-raised, #f7f7f8)',
                  borderRadius: '6px', padding: '12px 16px',
                  marginBottom: '8px', fontSize: '13px',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    {t.title}
                    <code style={{ fontWeight: 400, marginLeft: '8px',
                                   fontSize: '11px', color: '#888' }}>
                      {t.table}
                    </code>
                  </div>
                  <p style={{ margin: '4px 0', lineHeight: 1.5 }}>
                    <strong>Measures:</strong> {t.measures}
                  </p>
                  <p style={{ margin: '4px 0', lineHeight: 1.5 }}>
                    <strong>Algorithm:</strong> {t.algorithm}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <h3 id="data-dictionary" style={{ margin: '24px 0 4px', fontSize: '15px',
                                         fontWeight: 600 }}>
        Data dictionary
      </h3>
      <p className="muted" style={{ fontSize: '12px', margin: '0 0 8px' }}>
        Every serve table and column under the names readers use &mdash; the
        same page as the Insights Lab&rsquo;s Data Dictionary tab.
      </p>
      <DataDictionaryView />
    </>
  )
}

// ── Pipeline DAG view ─────────────────────────────────────────────────

const activeTabStyle: CSSProperties = {
  padding: '4px 12px', fontSize: '13px', fontWeight: 600,
  borderRadius: '4px 4px 0 0',
  background: 'var(--color-surface-raised, #f0f0f0)',
  color: 'var(--color-text-primary, #333)',
  textDecoration: 'none',
  borderBottom: '2px solid var(--color-text-primary, #333)',
}

const idleTabStyle: CSSProperties = {
  padding: '4px 12px', fontSize: '13px', fontWeight: 500,
  borderRadius: '4px 4px 0 0',
  background: 'transparent',
  color: '#888',
  textDecoration: 'none',
  borderBottom: '2px solid transparent',
}

type PipelineStep = {
  id: string
  label: string
  layer: string
  deps: string[]
  status: 'built' | 'pending' | 'running' | 'failed' | 'unknown'
  /** built, and its last run wrote no rows (a game with no shots) */
  empty?: boolean
  /** not measured for the game, as the export says it: refused for a
   *  missing input, latest build failed or cancelled, or never built */
  not_measured?: {
    kind?: 'refused' | 'build_failed' | 'build_cancelled' | 'never_built'
    missing_input: string; reason: string
  } | null
  /** measured, with notes given beside the table: a note
   *  its build recorded, or rows from an earlier build when the latest
   *  rebuild failed or was cancelled; null when they could not be read */
  notes?: { kind: string; reason: string }[] | null
  buildable?: boolean
  last_built_at: string | number | null
  // Steps whose rebuild makes this one due again without being a dep
  // (serve-enrich copies them): it is queued after any of them.
  rerun_after?: string[]
}

/** `ids` plus, after them, every step due again because one of them is
 *  rebuilt (rerun_after) and whose own deps are ready or queued. A forced
 *  or single rebuild of an L6 step otherwise left serve-enrich's copies
 *  stale until someone ran it by hand. */
export function withReruns(
  steps: Pick<PipelineStep, 'id' | 'deps' | 'buildable' | 'rerun_after'>[],
  ids: string[], ready: (id: string) => boolean,
): string[] {
  const queued = new Set(ids)
  const due = (s: (typeof steps)[number]) => s.buildable !== false
    && (s.rerun_after ?? []).some((d) => queued.has(d))
    && s.deps.every((d) => ready(d) || queued.has(d))
  return [...ids, ...steps.filter((s) => !queued.has(s.id) && due(s))
    .map((s) => s.id)]
}

/** When a batch's first step started — the whole run's clock — or null
 *  before any has. */
export function batchStart(rs: Pick<BuildRun, 'started_at'>[]): string | null {
  return rs.map((r) => r.started_at).filter((s): s is string => s != null)
    .sort()[0] ?? null
}

/** `ids` plus every pending step they need that is neither built nor
 *  queued ahead (`ready`), transitively, in pipeline (`steps`) order.
 *  "All \u2193" from an L4 step queued l6-pass-option without
 *  l6-spatial-control (it hangs off kinematics, not L4), and the server
 *  refused the batch for a missing input (game 234, 2026-10-02). */
export function withPendingDeps(
  steps: Pick<PipelineStep, 'id' | 'deps' | 'buildable' | 'status'>[],
  ids: string[], ready: (id: string) => boolean,
): string[] {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const want = new Set(ids)
  const todo = [...ids]
  while (todo.length) {
    for (const d of byId.get(todo.pop()!)?.deps ?? []) {
      const dep = byId.get(d)
      if (want.has(d) || ready(d) || !dep || dep.buildable === false
          || dep.status !== 'pending') continue
      want.add(d)
      todo.push(d)
    }
  }
  return steps.filter((s) => want.has(s.id)).map((s) => s.id)
}

const STATUS_COLORS: Record<string, string> = {
  built: '#22c55e',    // green
  pending: '#9ca3af',  // gray
  running: '#eab308',  // yellow
  failed: '#ef4444',   // red
  unknown: '#f97316',  // orange — the check could not run; NOT "not built"
  // orange, as unknown: the build ran, but an input it needs is missing
  not_measured: '#f97316',
  // green, as built: the table is measured; the label and the reason
  // say the note (yellow is a running build's)
  noted: '#22c55e',
}

const LAYER_COLORS: Record<string, string> = {
  l1: '#6366f1',   l2: '#8b5cf6',   l3: '#a855f7',
  promote: '#d946ef', l4: '#ec4899',  l5: '#f43f5e',
  l6: '#f97316',   serve: '#14b8a6',
}

/** Format a timestamp (epoch seconds or ISO string) as relative time. */
function fullTimestamp(ts: string | number | null): string {
  if (ts == null) return ''
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC'
}

// Canonical layer order for pipeline rows
const LAYER_ORDER = [
  'l1', 'l2', 'l3', 'l4', 'l5', 'l6', 'serve',
]

const LAYER_LABELS: Record<string, string> = {
  l1: 'L1 Ingest', l2: 'L2 Clean', l3: 'L3 Canonical',
  l4: 'L4 Events', l5: 'L5 Structure', l6: 'L6 Signals', serve: 'Serve',
}

/** Group steps by layer, one row per layer. Within a layer, sort
 *  topologically by same-layer deps so a step depending on another
 *  step in its own layer renders after it. */
function computeLayerRows(steps: PipelineStep[]): { layer: string; steps: PipelineStep[] }[] {
  const byLayer = new Map<string, PipelineStep[]>()
  for (const s of steps) {
    const arr = byLayer.get(s.layer) ?? []
    arr.push(s)
    byLayer.set(s.layer, arr)
  }
  const rows: { layer: string; steps: PipelineStep[] }[] = []
  for (const layer of LAYER_ORDER) {
    const group = byLayer.get(layer)
    if (!group || group.length === 0) continue
    // Topological sort within the layer by same-layer deps
    const ids = new Set(group.map((s) => s.id))
    const sorted: PipelineStep[] = []
    const visited = new Set<string>()
    const visit = (s: PipelineStep) => {
      if (visited.has(s.id)) return
      visited.add(s.id)
      for (const dep of s.deps) {
        if (ids.has(dep)) {
          const depStep = group.find((g) => g.id === dep)
          if (depStep) visit(depStep)
        }
      }
      sorted.push(s)
    }
    for (const s of group) visit(s)
    rows.push({ layer, steps: sorted })
  }
  return rows
}

type LabelsFreshnessPayload = {
  /** Goal and shot labels: both are read back by the Shots build. */
  goal_labels: number
  last_label_at: string | null
  l4_built_at: string | null
  stale: boolean
  /** Which input is stale: the goal labels (Shots rebuilds), or the goal
   *  reviews — confirm/reject on detected goals — that Game State and the
   *  served goals fold in. Absent from an older server: labels. */
  labels_stale?: boolean
  reviews_stale?: boolean
  goal_reviews?: number
  shot_reviews?: number
  /** Human reviewed ticked or unticked on a game whose xG waits on it
   *  (serve.overlays.xg_published), and when. */
  human_review_stale?: boolean
  human_review_at?: string | null
  /** L3 cut its bands on another length than the pitch the game's
   *  positions are anchored on today (ops_qa_api._zones_freshness). */
  zones_stale?: boolean
  zones?: { built_m: number; anchored_m: number } | null
  /** Serve copies no reader uses: their stamp moved (views.copies_unread). */
  copies_stale?: boolean
  /** The step the rebuild starts at — the earliest stale step in pipeline
   *  order, as the server picks it. */
  root?: string
  steps: string[]
  /** Newest run of ANY status of the first stale step (Shots for labels)
   *  — a stale banner says when its own rebuild failed or is running. */
  last_run?: { status: string; at: string | null } | null
}

/** The "labels changed, layers are stale" banner: goal labels written
 *  after the last l4-shots build (Match Lab / its wizard) change what a
 *  rebuild would produce, so the stack needs L4 Shots and everything on
 *  it rebuilt. Goal REVIEWS (a detected goal rejected or corrected in
 *  Match Lab) do the same to Game State and the served goals — a review
 *  pass used to leave no banner at all (Bradley, 2026-10-24). Renders
 *  nothing while fresh. Refetches whenever the
 *  build queue goes idle: the one fetch per page load left a finished
 *  rebuild's banner exactly as it was (game 567 on prod, 2026-10-20),
 *  which read as the rebuild having done nothing. */
export function LabelsFreshness({ gameId, onRebuild, busy }: {
  gameId: number
  /** Queue the stale steps (the parent owns the build queue). */
  onRebuild: (steps: string[]) => void
  busy?: boolean
}) {
  const [fresh, setFresh] = useState<LabelsFreshnessPayload | null>(null)
  useEffect(() => { setFresh(null) }, [gameId])
  useEffect(() => {
    if (busy) return   // refetch when the queue lands, not while it runs
    let on = true
    api<LabelsFreshnessPayload>(
      `/api/v1/opsqa/games/${gameId}/labels-freshness`)
      .then((d) => { if (on) setFresh(d) })
      .catch(() => { /* the banner is advisory; no freshness, no banner */ })
    return () => { on = false }
  }, [gameId, busy])
  if (!fresh?.stale) return null
  const reviews = !!fresh.reviews_stale
  const human = !!fresh.human_review_stale
  const zones = !!fresh.zones_stale
  const copies = !!fresh.copies_stale
  // an older server sends none of the others: labels
  const labels = fresh.labels_stale ?? !(reviews || human || zones || copies)
  const nReviews = fresh.goal_reviews ?? 0
  // The step the rebuild starts at: the server's root names it (release
  // review of b95931a9: a fixed order here could name a step the rebuild
  // does not start at); an older server sends none.
  const ROOT_NAMES: Record<string, string> = {
    'l3': 'L3', 'l4-shots': 'Shots', 'l4-gamestate': 'Game State',
    'serve-event-parquets': 'Served events', 'serve-enrich': 'Serve copies',
  }
  // an unnamed root is named by its id, never guessed
  const what = fresh.root ? (ROOT_NAMES[fresh.root] ?? fresh.root)
    : labels ? 'Shots' : zones ? 'L3' : reviews ? 'Game State'
      : human ? 'Served events' : 'Serve copies'
  const run = fresh.last_run
  const runNote = !run || run.status === 'succeeded' ? null
    : run.status === 'failed' || run.status === 'cancelled'
      ? <> The last {what} rebuild <strong>{run.status}</strong>
          {run.at ? ` (${new Date(run.at).toLocaleString()})` : ''} — see
          the build queue below.</>
      : <> A {what} rebuild is {run.status} — this banner clears when it
          lands.</>
  const nShotReviews = fresh.shot_reviews ?? 0
  const reviewCounts = [
    nReviews ? `${nReviews} goal review${nReviews === 1 ? '' : 's'}` : '',
    nShotReviews
      ? `${nShotReviews} shot review${nShotReviews === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' and ')
  const reviewNote = reviews && (
    <>{labels ? ' Reviews changed too: ' : ' '}
      {reviewCounts} (rejected or corrected) since Game State, the
      chances&apos; xG and the served events were built.</>)
  // Each other input says why (release review of 112dab3f: a Human
  // reviewed toggle read as "goal reviews changed" with 0 reviews).
  const humanNote = human && (
    <> Human reviewed was changed
      {fresh.human_review_at
        ? ` (${new Date(fresh.human_review_at).toLocaleString()})` : ''}
      {' '}since the served events were built: the match's xG is served or
      withheld on it.</>)
  const zonesNote = zones && (fresh.zones
    ? <> L3 cut its pitch bands on a {fresh.zones.built_m} m pitch; the
        positions are anchored on {fresh.zones.anchored_m} m.</>
    : <> L3 cut its pitch bands on another pitch than its positions are
        anchored on.</>)
  const copiesNote = copies && (
    <> The prepared serve tables no longer match the game, so readers use
      the plain ones until they are written again.</>)
  const headline = labels ? 'Labels changed:' : zones ? 'Pitch zones changed:'
    : reviews ? (!nShotReviews ? 'Goal reviews changed:'
                 : !nReviews ? 'Shot reviews changed:' : 'Reviews changed:')
    : human ? 'Human review changed:'
      : 'Serve tables out of date:'
  return (
    <div role="status"
         style={{ marginBottom: 'var(--space-2)', padding: '6px 10px',
                  background: 'rgba(234,179,8,0.08)',
                  border: '1px solid rgba(234,179,8,0.35)',
                  borderRadius: '4px', fontSize: '11px' }}>
      <strong style={{ color: '#eab308' }}>
        {headline}
      </strong>
      {labels && <>
        {' '}{fresh.goal_labels} goal and shot label{fresh.goal_labels === 1 ? '' : 's'}
        {fresh.l4_built_at
          ? <> newer than the last Shots build
              ({new Date(fresh.l4_built_at).toLocaleString()})</>
          : <> with no Shots build recorded since</>}
      </>}
      {reviewNote}{humanNote}{zonesNote}{copiesNote}
      {' — '}{fresh.steps.length} steps are stale
      {` (${what}, then everything built on it).`}{runNote}
      <button className="adm-btn" disabled={busy}
              style={{ fontSize: '11px', padding: '1px 8px',
                       marginLeft: '8px' }}
              onClick={() => onRebuild(fresh.steps)}>
        Queue rebuild ({what} {'→'} serve)
      </button>
    </div>
  )
}

export function PipelineView({ gameId, canRerunTracking = false }: {
  gameId: number
  /** Platform admins: "Re-run TRDAAS" beside the L1 Ingest box. */
  canRerunTracking?: boolean
}) {
  const [steps, setSteps] = useState<PipelineStep[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Force (overwrite): offered only where the server allows it (dev and
  // local); off on every page load.
  const [overwriteAllowed, setOverwriteAllowed] = useState(false)
  const [force, setForce] = useState(false)
  // Large job: the batch runs as one 24 GiB execution, not on the 8 GiB
  // worker (a step the worker's memory kills); off on every page load.
  const [large, setLarge] = useState(false)
  const runs = useBuildRuns()

  const load = useCallback(() => {
    setError(null)
    api<{ steps: PipelineStep[]; overwrite_allowed?: boolean }>(
      `/api/v1/opsqa/games/${gameId}/pipeline-status`)
      .then((d) => {
        setSteps(d.steps)
        setOverwriteAllowed(d.overwrite_allowed === true)
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [gameId])

  const { stackVersion } = useActionLog()
  useEffect(() => { load() }, [load, stackVersion])

  const stepMap = useMemo(() => {
    if (!steps) return new Map<string, PipelineStep>()
    return new Map(steps.map((s) => [s.id, s]))
  }, [steps])

  if (error) return <p className="error">{error}</p>
  if (!steps || !runs) return <p className="muted">Loading pipeline status…</p>

  // Something is queued or running. Controls stay live: a new build QUEUES
  // behind it (the ledger runs a game's batches one at a time, in order).
  const busy = runs.activeBatch !== null
  const active = runs.activeSteps
  // A dependency is satisfied if it is built OR queued/running ahead.
  const depOk = (d: string) =>
    stepMap.get(d)?.status === 'built' || active.has(d)
  const builtCount = steps.filter((s) => s.status === 'built').length

  // A step plus everything downstream of it, pending ones only (the
  // start step always), buildable, in DAG order. "unknown" (status
  // check could not run, e.g. a truncated R2 listing) is NOT pending:
  // re-running a step that may be built costs minutes.
  const reach = (ids: string[]) => {
    const down = new Set<string>(ids)
    let changed = true
    while (changed) {
      changed = false
      for (const s of steps) {
        if (!down.has(s.id) && s.deps.some((d) => down.has(d))) {
          down.add(s.id); changed = true
        }
      }
    }
    return down
  }
  const downstreamOf = (startId: string) => {
    const down = reach([startId])
    return withPendingDeps(steps, steps
      .filter((s) => down.has(s.id) && s.buildable !== false)
      .filter((s) => s.status === 'pending' || s.id === startId)
      .filter((s) => !active.has(s.id))
      .map((s) => s.id), depOk)
  }
  const pendingAll = steps
    .filter((s) => s.buildable !== false && s.status === 'pending'
      && !active.has(s.id))
    .map((s) => s.id)
  // Force queues what was asked plus EVERYTHING built on it, built or
  // not: an overwritten layer leaves its dependents built from the old
  // one (Ben, 2026-09-29). Building FROM a step or a layer (All ↓, the
  // layer button) does the same wherever overwriting is allowed, Force
  // ticked or not: everything above is rebuilt, not only what is pending
  // (Ben, 2026-10-03). Without overwrite a built step's publish would be
  // refused, so there it stays the pending ones.
  const queue = (ids: string[], what: string, andAbove = false) => {
    // overwriteAllowed too: a reload that withdraws it hides the checkbox
    // but leaves `force` set, and every click would 403 unexplained.
    const ready = (d: string) => depOk(d)
    if (!((force || andAbove) && overwriteAllowed)) {
      return runs.start(withReruns(steps.filter((s) => !active.has(s.id)),
                                   ids, ready), what, false, large)
    }
    const down = reach(ids)
    return runs.start(
      withReruns(steps.filter((s) => !active.has(s.id)),
        steps.filter((s) => down.has(s.id) && s.buildable !== false
          && !active.has(s.id)).map((s) => s.id), ready),
      `${what} + everything downstream`, true, large)
  }
  const unknownCount = steps.filter((s) => s.status === 'unknown').length
  const allBuildable = steps
    .filter((s) => s.buildable !== false).map((s) => s.id)

  const batchRuns = (b: string) => runs.runs
    .filter((r) => r.batch_id === b)
    .sort((x, y) => x.position - y.position)

  return (
    <div>
      {/* The server's stale list, not just the root: where overwrite is
          off, queue() takes its first branch and would enqueue the root
          alone while the button promises Shots -> serve (SDET #737). */}
      <LabelsFreshness gameId={gameId} busy={busy}
        onRebuild={(staleSteps) =>
          queue(staleSteps, 'goal labels changed', true)} />
      {builtCount === steps.length && (
        <p style={{ margin: '0 0 var(--space-2)' }}>
          <a className="adm-btn"
             href={`${API_BASE}/api/v1/serve/games/${gameId}/tables.zip`}
             title={'Every serve table this game has — the exact parquet '
                    + 'files the platform reads — plus manifest.json and '
                    + 'the data dictionary, so work built on them plugs '
                    + 'back in.'}>
            Download serve tables (ZIP)
          </a>
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
                    marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
        <span className="muted">
          {builtCount}/{steps.length} steps built
        </span>
        {unknownCount > 0 && (
          <span style={{ color: STATUS_COLORS.unknown, fontSize: '11px' }}
                title="The status check could not run (a database check failed or the R2 listing was truncated). These steps are not treated as pending.">
            {unknownCount} unknown
          </span>
        )}
        {pendingAll.length > 0 && (
          <button className="adm-btn"
                  style={{ fontSize: '11px', padding: '2px 10px' }}
                  onClick={() => queue(pendingAll, 'pending')}>
            {busy ? 'Queue All Pending' : 'Build All Pending'}
          </button>
        )}
        {!busy && pendingAll.length === 0 && (
          <button className="adm-btn"
                  style={{ fontSize: '11px', padding: '2px 10px' }}
                  onClick={() => queue(allBuildable, 'rebuild all')}>
            Rebuild All
          </button>
        )}
        {overwriteAllowed && (
          <label style={{ fontSize: '11px', display: 'flex', gap: '4px',
                          alignItems: 'center',
                          color: force ? '#ef4444' : undefined }}
                 title="Replace a step's existing outputs in R2 instead of refusing, and queue everything downstream of it too. Dev and local only; L1 deliveries are never overwritten.">
            <input type="checkbox" checked={force}
                   onChange={(e) => setForce(e.target.checked)} />
            Force (overwrite)
          </label>
        )}
        <label style={{ fontSize: '11px', display: 'flex', gap: '4px',
                        alignItems: 'center' }}
               title="Run the batch as one execution of the large build job (8 CPUs, 24 GiB, 1 hour limit) instead of on the 8 GiB worker — for a step the worker runs out of memory on (exit -9). Queue only the steps that still need it: a full rebuild of a 60 fps match may not fit the hour.">
          <input type="checkbox" checked={large}
                 onChange={(e) => setLarge(e.target.checked)} />
          Large job (24 GiB)
        </label>
      </div>

      <WorkerActivity gameId={gameId} />

      {/* The queue: every batch in order, its steps, time so far, cancel */}
      {busy && (
        <div style={{ fontSize: '11px', marginBottom: 'var(--space-2)',
                      padding: '6px 8px', borderRadius: '4px',
                      background: '#1a1a1e', color: '#d4d4d4' }}>
          {runs.activeBatches.map((b, i) => {
            const rs = batchRuns(b)
            const waiting = rs.every((r) => r.status === 'queued')
            return (
              <div key={b} style={{ marginBottom: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center',
                              gap: '6px' }}>
                  <span style={{ color: '#888' }}>
                    {i === 0 ? 'Now' : `Queued #${i}`}
                  </span>
                  {waiting && i === 0 && rs[0] && (
                    <span style={{ color: '#eab308' }}>
                      {'\u23f3 '}{waitingFor(rs[0].runner)}
                      <Elapsed since={rs[0].queued_at} />
                    </span>
                  )}
                  {waiting && i > 0 && (
                    <span style={{ color: '#888' }}>waits for the batch above</span>
                  )}
                  {!waiting && batchStart(rs) && (
                    <span style={{ color: '#888' }}>
                      {rs.filter((r) => r.status === 'succeeded').length} of {rs.length} done
                      <Elapsed since={batchStart(rs)!} /> elapsed
                    </span>
                  )}
                  <button className="adm-btn"
                          style={{ fontSize: '9px', padding: '0 6px',
                                   marginLeft: 'auto', color: '#ef4444' }}
                          title={waiting ? 'Remove this batch from the queue'
                            : 'Stop this batch. A step stopped mid-build may leave partial output; rebuild it.'}
                          onClick={() => runs.cancel(b)}>
                    {waiting ? 'Remove' : 'Cancel'}
                  </button>
                </div>
                {rs.map((r) => (
                  <div key={r.run_id} style={{ paddingLeft: '10px' }}>
                    <span style={{ color: RUN_COLORS[r.status] }}>{r.status}</span>
                    {' '}{r.step}
                    {r.status === 'running' && r.started_at && (
                      <Elapsed since={r.started_at} />
                    )}
                    {r.duration_ms != null && ` · ${secs(r.duration_ms)}`}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {/* Pipeline grid — one row per layer */}
      <div style={{ display: 'flex', flexDirection: 'column',
                    gap: '8px' }}>
        {computeLayerRows(steps).map(({ layer, steps: layerSteps }) => {
          const buildable = layerSteps.filter((s) => s.buildable !== false)
          const inLayer = new Set(layerSteps.map((s) => s.id))
          // Deps outside this layer must be built or queued ahead
          const blockedBy = [...new Set(layerSteps.flatMap((s) => s.deps))]
            .filter((d) => !inLayer.has(d) && !depOk(d))
          const toQueue = buildable.filter((s) => !active.has(s.id))
          const layerBuilt = buildable.every((s) => s.status === 'built')
          return (
          <div key={layer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                          marginBottom: '3px', paddingLeft: '2px' }}>
              <span style={{ fontSize: '10px', fontWeight: 700,
                             color: LAYER_COLORS[layer] ?? '#888',
                             textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {LAYER_LABELS[layer] ?? layer}
              </span>
              {buildable.length > 0 && (
                <button className="adm-btn"
                        style={{ fontSize: '9px', padding: '0 6px' }}
                        disabled={toQueue.length === 0 || blockedBy.length > 0}
                        title={blockedBy.length > 0
                          ? `Needs ${blockedBy.join(', ')} built (or queued) first`
                          : toQueue.length === 0
                            ? 'Every step in this layer is already queued or running'
                            : overwriteAllowed
                              ? `Rebuild ${toQueue.length} step(s) in this layer and everything above it`
                              : `Build ${toQueue.length} step(s) in this layer`}
                        onClick={() => queue(toQueue.map((s) => s.id),
                          `in ${LAYER_LABELS[layer] ?? layer}`, true)}>
                  {busy ? 'Queue layer'
                    : layerBuilt ? 'Rebuild layer' : 'Build layer'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '4px',
                          flexWrap: 'wrap' }}>
              {layerSteps.map((step) => {
                const sid = step.id
                const run = runs.latest.get(sid)
                const live = run && (run.status === 'running' || run.status === 'queued'
                  || run.status === 'cancelling')
                  ? run : null
                const lastFailed = run?.status === 'failed' ? run : null
                const stat = runs.stats.get(sid)
                // "? Unknown" (the check could not run) outranks it, and a
                // step never built reads Pending: the same fact, said plainly
                const notMeasured = step.status !== 'unknown'
                  && step.not_measured?.kind !== 'never_built'
                  ? step.not_measured ?? null : null
                // built, with notes: the label says so and each reason
                // follows it
                const notes = !live && !notMeasured && step.status === 'built'
                  ? step.notes ?? [] : []
                // a failed build stays red on the border: the severity an
                // ops reader acts on; the label still says not measured
                const color = live ? '#eab308'
                  : notMeasured?.kind === 'build_failed' ? STATUS_COLORS.failed
                    : notMeasured ? STATUS_COLORS.not_measured
                      : STATUS_COLORS[step.status] ?? '#9ca3af'
                const layerColor = LAYER_COLORS[step.layer] ?? '#888'
                const depsReady = step.deps.every(depOk)
                // Queueable unless already queued/running (no duplicates).
                const canBuild = step.buildable !== false && depsReady
                  && !active.has(sid)
                return (
                  <div key={sid}
                       title={stat
                         ? `Build time (${stat.runner ?? '?'}, ${stat.n} run${stat.n === 1 ? '' : 's'}): median ${secs(stat.median_ms)}, p90 ${secs(stat.p90_ms)}, max ${secs(stat.max_ms)}, last ${secs(stat.last_ms)}`
                         : 'No build-time statistics yet'}
                       style={{
                    display: 'flex', flexDirection: 'column',
                    padding: '6px 10px', borderRadius: '6px',
                    border: `2px solid ${color}`,
                    background: step.status === 'built'
                      ? `${color}30` : '#2a2a2e',
                    color: '#e8e8e8',
                    minWidth: '110px', position: 'relative',
                    opacity: 1,
                  }}>
                    <span style={{ fontSize: '10px', fontWeight: 600,
                                    color: layerColor, textTransform: 'uppercase',
                                    letterSpacing: '0.5px' }}>
                      {step.layer}
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: 500 }}>
                      {step.label}
                    </span>
                    <span style={{ fontSize: '10px', color,
                                    fontWeight: 600, marginTop: '2px' }}
                          title={!live && notMeasured
                            ? notMeasured.reason : undefined}>
                      {live?.status === 'cancelling' ? '\u23f9 Cancelling\u2026'
                        : live?.status === 'running' ? <>{'\u23f3 Running'}
                          {live.started_at && <Elapsed since={live.started_at} />}</>
                        : live ? <>{'\u2026 Queued'}<Elapsed since={live.queued_at} /></>
                          : notMeasured ? `Not measured: ${notMeasured.missing_input}`
                          // an empty table keeps its 0 rows beside the note: it is
                          // the signal an ops reader acts on (SDET review of #727)
                          : notes.length ? (step.empty ? 'Built \u2014 0 rows, with a note'
                                                       : 'Built, with a note')
                          : step.status === 'built' ? (step.empty ? '\u2713 Built \u2014 0 rows' : '\u2713 Built')
                            : step.status === 'unknown' ? '? Unknown'
                              : '\u25cb Pending'}
                    </span>
                    {notes.map((n) => (
                      <span key={n.kind} style={{ fontSize: '9px', color,
                                                  maxWidth: '200px',
                                                  marginTop: '1px' }}>
                        {n.reason}
                      </span>
                    ))}
                    {step.last_built_at && !live && (
                      <span style={{ fontSize: '9px', color: '#888',
                                      marginTop: '1px' }}
                            title={fullTimestamp(step.last_built_at)}>
                        {relativeTime(step.last_built_at)}
                      </span>
                    )}
                    {stat && !live && (
                      <span style={{ fontSize: '9px', color: '#888' }}>
                        ~{secs(stat.median_ms)}
                      </span>
                    )}
                    {lastFailed && (
                      <span style={{ fontSize: '9px', color: '#ef4444' }}
                            title={lastFailed.error ?? ''}>
                        last run failed
                      </span>
                    )}
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '4px',
                                  marginTop: '4px' }}>
                      <button className="adm-btn"
                              style={{ fontSize: '9px', padding: '1px 4px' }}
                              disabled={!canBuild}
                              onClick={() => queue([sid], sid)}>
                        {step.status === 'built' ? 'Rebuild' : 'Build'}
                      </button>
                      <button className="adm-btn"
                              style={{ fontSize: '9px', padding: '1px 4px' }}
                              disabled={step.buildable === false || active.has(sid)}
                              onClick={() => queue(downstreamOf(sid), `from ${sid}`, true)}
                              title={overwriteAllowed
                                ? `Rebuild ${sid} and everything above it`
                                : `Build ${sid} and what is pending above it`}>
                        All ↓
                      </button>
                    </div>
                  </div>
                )
              })}
              {layer === 'l1' && canRerunTracking && <RerunTrdaas gameId={gameId} />}
            </div>
          </div>
          )
        })}
      </div>
    </div>
  )
}

const RUN_COLORS: Record<RunStatus, string> = {
  queued: '#8b8b8b', running: '#eab308', cancelling: '#f97316',
  succeeded: '#22c55e',
  failed: '#ef4444', skipped: '#8b8b8b', cancelled: '#ef4444',
}


export default function LayerStackOps({ session = null }: { session?: Session | null } = {}) {
  const [sp, setSp] = useSearchParams()
  const [games, setGames] = useState<StackGame[] | null>(null)
  const [orgs, setOrgs] = useState<OpsOrganization[]>([])
  const [error, setError] = useState<string | null>(null)
  const [pipelineOpen, setPipelineOpen] = useState(true)

  // URL-driven state: ?view=match|layer&game=15&layer=l4&table=event_action
  // W35: the index is the landing view; match/layer stay URL-driven.
  const viewParam = sp.get('view')
  const view: 'index' | 'match' | 'builds' | 'docs' | 'guide' =
    viewParam === 'guide' ? 'guide'
    : viewParam === 'docs' ? 'docs'
      : viewParam === 'builds' ? 'builds'
        : viewParam === 'match' || viewParam === 'pipeline' ? 'match'
          : 'index'
  const gameId = sp.get('game') ? Number(sp.get('game')) : null
  const clubParam = sp.get('club') ?? ''

  const updateSp = useCallback((updates: Record<string, string | null>) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(updates)) {
        if (v == null || v === '') next.delete(k)
        else next.set(k, v)
      }
      return next
    }, { replace: true })
  }, [setSp])

  const setView = useCallback((v: 'index' | 'match' | 'builds' | 'docs' | 'guide') => {
    updateSp({ view: v, game: null, layer: null, table: null })
  }, [updateSp])

  // Opening a row selects its club too: a club filter left over from an
  // earlier visit used to swap the clicked game for that club's first one.
  const openMatch = useCallback((id: number) => {
    const org = games?.find((g) => g.game_id === id)?.organization_id
    updateSp({ view: 'match', game: String(id),
               club: org != null ? String(org) : null })
  }, [updateSp, games])

  const setClub = useCallback((club: string | null) => {
    const inClub = (games ?? []).filter((g) =>
      club == null || g.organization_id === Number(club))
    const keep = gameId != null && inClub.some((g) => g.game_id === gameId)
    updateSp({ club, game: keep ? String(gameId)
      : inClub[0] ? String(inClub[0].game_id) : null })
  }, [games, gameId, updateSp])

  const setGameId = useCallback((id: number | null) => {
    updateSp({ game: id != null ? String(id) : null })
  }, [updateSp])

  useEffect(() => {
    api<{ games: StackGame[] }>('/api/v1/opsqa/games')
      .then((d) => {
        setGames(d.games)
        // Only set a default game if URL doesn't already have one
        if (!sp.get('game') && d.games[0]) {
          updateSp({ game: String(d.games[0].game_id) })
        }
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
    api<{ organizations: OpsOrganization[] }>('/api/v1/opsqa/organizations')
      .then((d) => setOrgs(d.organizations))
      .catch(() => {})
    // Scale-to-zero build workers take minutes to start: opening the page
    // starts one, so it is up by the time a build is queued. A no-op
    // unless drain mode is on, or when one is already up.
    api('/api/v1/opsqa/build-workers/warm', { method: 'POST' }).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // The selected club's badge sits beside the page title.
  const selectedOrg = clubParam
    ? orgs.find((o) => String(o.id) === clubParam) ?? null
    : null

  // Filter games by selected club
  const filteredGames = useMemo(() => {
    if (!games) return null
    if (!clubParam) return games
    const orgId = Number(clubParam)
    return games.filter((g) => g.organization_id === orgId)
  }, [games, clubParam])

  // Match view with no game chosen: pick the first. A game named in the
  // URL is never swapped out — a club change picks its own (setClub).
  useEffect(() => {
    const first = (filteredGames ?? games)?.[0]
    if (view === 'match' && !gameId && first) {
      updateSp({ game: String(first.game_id) })
    }
  }, [view, filteredGames, games, gameId, updateSp])

  return (
    <>
      <div className="ls-head">
        <h1 className="page-title" style={{ marginBottom: 0, display: 'flex',
                                            alignItems: 'center', gap: '10px' }}>
          Match Administration
          {selectedOrg && (
            <ClubLogo code={selectedOrg.code} url={selectedOrg.logo_url}
                      name={selectedOrg.name} />
          )}
        </h1>
        <div className="ls-head__controls">
          <div className="ls-tabs">
            <a href="/ops/layer-stack"
               onClick={(e) => { e.preventDefault(); setView('index') }}
               style={view === 'index' ? activeTabStyle : idleTabStyle}>
              Matches
            </a>
            <a href="/ops/layer-stack?view=match"
               onClick={(e) => { e.preventDefault(); setView('match') }}
               style={view === 'match' ? activeTabStyle : idleTabStyle}>
              Layer Stack
            </a>
            <a href="/ops/match-lab"
               style={idleTabStyle}>
              Match Lab
            </a>
            <a href={gameId ? `/ops/tracking-qa/${gameId}` : '/ops/tracking-qa'}
               style={idleTabStyle}>
              Tracking QA
            </a>
            <a href="/ops/layer-stack?view=builds"
               onClick={(e) => { e.preventDefault(); setView('builds') }}
               style={view === 'builds' ? activeTabStyle : idleTabStyle}>
              Builds
            </a>
            {/* Manage Games' settings are the Matches tab's gear; QA Studio
                is gone (Ben, 2026-10-17). */}
            <a href="/ops/layer-stack?view=docs"
               onClick={(e) => { e.preventDefault(); setView('docs') }}
               style={view === 'docs' ? activeTabStyle : idleTabStyle}>
              Docs
            </a>
            <a href="/ops/layer-stack?view=guide"
               onClick={(e) => { e.preventDefault(); setView('guide') }}
               style={view === 'guide' ? activeTabStyle : idleTabStyle}>
              Guide
            </a>
          </div>
          {view === 'match' && games && games.length > 0 && (
            <div className="ls-pickers">
              {orgs.length > 1 && (
                <select className="adm-select ls-picker"
                        value={clubParam}
                        style={{ minWidth: '140px' }}
                        onChange={(e) => setClub(e.target.value || null)}>
                  <option value="">All clubs</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              )}
              <select className="adm-select ls-picker" value={gameId ?? ''}
                      style={{ minWidth: '280px' }}
                      onChange={(e) => setGameId(Number(e.target.value))}>
                {/* A game opened from the index that this list does not
                    carry still shows as selected, not as the first row. */}
                {gameId != null
                  && !(filteredGames ?? games).some((g) => g.game_id === gameId)
                  && <option value={gameId}>#{gameId}</option>}
                {(filteredGames ?? games).map((g) => (
                  <option key={g.game_id} value={g.game_id}>
                    #{g.game_id} {g.home_team ?? '?'} v {g.away_team ?? '?'}
                    {g.match_date ? ` \u00b7 ${g.match_date}` : ''}
                    {g.vendors.length ? ` \u00b7 ${g.vendors.join('/')}` : ''}
                    {g.stack_status ? ` [${STACK_STATUS_LABELS[g.stack_status] ?? g.stack_status}]` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {!error && view === 'index' && (
        <LayersLabIndex onOpen={openMatch} club={clubParam}
                        canSettings={!!(session?.user.is_staff || session?.user.is_platform_admin)}
                        canUploadBadges={!!session?.user.is_platform_admin}
                        canDeleteMatches={!!session?.user.is_platform_admin}
                        onClub={(c) => updateSp({ club: c })} />
      )}
      {!error && games === null && view === 'match' &&
        <p className="muted">Loading…</p>}
      {!error && games !== null && view === 'match' && (
        games.length === 0
          ? <p className="muted">No games in the layer stack yet.</p>
          : gameId !== null && (
            <ActionLogProvider key={gameId} gameId={gameId}><BuildRunsProvider gameId={gameId}>
            <div className="ls-panes">
              {/* Left: Layer Stack detail */}
              <div className="ls-pane-main">
                <MatchView gameId={gameId}
                           canCopyVideo={!!(session?.user.is_staff || session?.user.is_platform_admin)}
                           settings={session?.user.is_staff || session?.user.is_platform_admin
                             ? { canUploadBadges: !!session?.user.is_platform_admin,
                                 canDelete: !!session?.user.is_platform_admin,
                                 // the match is gone: back to the list
                                 onDeleted: () => setView('index') }
                             : undefined} />
              </div>
              {/* Right: Pipeline panel — collapsible. On a phone it
                  stacks UNDER the detail at full width (layer-stack.css). */}
              <div className={`ls-pane-pipeline${pipelineOpen ? ' is-open' : ''}`}
                   style={{
                flex: pipelineOpen ? '0 0 380px' : '0 0 28px',
                minWidth: pipelineOpen ? '340px' : '28px',
                borderLeft: '1px solid var(--color-border, #333)',
                position: 'relative',
                transition: 'flex-basis 0.15s ease, min-width 0.15s ease',
                overflow: 'hidden',
              }}>
                <button
                  className="adm-btn"
                  onClick={() => setPipelineOpen(!pipelineOpen)}
                  style={{
                    position: 'sticky', top: 0, zIndex: 1,
                    width: '100%',
                    fontSize: '10px', padding: '4px 8px',
                    textAlign: 'left', borderRadius: 0,
                    borderBottom: '1px solid var(--color-border, #333)',
                    background: 'var(--color-surface-raised, #1a1a1e)',
                  }}
                  title={pipelineOpen ? 'Collapse pipeline' : 'Expand pipeline'}>
                  {pipelineOpen ? '▸ Pipeline' : '◂'}
                </button>
                {pipelineOpen && (
                  <div style={{ padding: '8px 12px', overflow: 'auto' }}>
                    <PipelineView gameId={gameId}
                                  canRerunTracking={!!session?.user.is_platform_admin} />
                  </div>
                )}
              </div>
            </div>
            </BuildRunsProvider></ActionLogProvider>
          )
      )}
      {view === 'builds' && <BuildBoard onOpen={openMatch}
                                       canSetWorkers={!!session?.user.is_platform_admin} />}
      {!error && view === 'docs' && <DocsView />}
      {/* After the games list has loaded: that call is the server's
          platform.read check, so the guide shows only to the people this
          page is for (a refusal leaves `error` set and `games` null). */}
      {!error && games !== null && view === 'guide' && <LayerStackGuide />}
    </>
  )
}
